import { useState, useEffect, useRef } from "react";
import {
  Settings, Palette, Trash2, Building2, AlertTriangle,
  Upload, X, Save, Loader2, CheckCircle, Image as ImageIcon,
  Instagram, Facebook, Mail, Phone, MapPin, Calendar, Hash,
  ShoppingCart, Users, DollarSign, Map, RefreshCw,
  Eye, ToggleLeft, ToggleRight, Plus, Edit, Clock,
  AlertCircle, CheckCircle2, XCircle, RotateCcw, Type, Printer, Shield,
  Wifi, WifiOff, Link, Unlink, Play, Download, Moon, Sun, Check, History,
  Monitor, ChevronDown, ChevronRight, QrCode, MessageCircle, Store,
  Pencil, Truck, Wallet, ExternalLink, Copy, Navigation, Package, ShieldAlert, Banknote,
  SlidersHorizontal, Scale,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { InputSecret } from "../components/ui/input-secret";
import { useToast } from "../hooks/use-toast";
import IFoodConfig from "./IFoodConfig";
import { ConfiguracaoFiscal, FiscalLock } from "../features/fiscal";
import { hasAccess, PlanBadge, PlanGateOverlay, getPlanLabel } from "../components/PlanGate";
import { invalidateImpressaoCache, buildLogoUrl } from "../utils/cupomFormatters";
import { AvisoPreparoDesligado, PreferenciasTab } from "@/features/preferencias";
import { CaixaRegistradoraIcon, MesaIcon, MotoEntregaIcon } from "@/components/icons/ModulosIcons";
import { ControleTab } from "@/features/controle";
import { IntegracaoBalanca } from "@/features/balanca";
import { escolhaDoUsuario, guardarPadraoDaLoja } from "@/features/tema";
import { CarregandoBloco, CarregandoFormulario, CarregandoTabela } from "@/components/carregando";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Sistema({ userPlan = "enterprise" }) {
  const [activeSubTab, setActiveSubTab] = useState("aparencia");
  const { toast } = useToast();

  const subTabs = [
    { id: "impressao", label: "Impressão", icon: Printer },
    { id: "aparencia", label: "Aparência", icon: Palette },
    { id: "integracao", label: "Integração", icon: Link },
    // Aba "Bot" removida — toda a persona/contexto agora vive em Chatbot → Contexto
    { id: "empresa", label: "Empresa", icon: Building2 },
    { id: "controle", label: "Controle", icon: ShieldAlert, requiredPlan: "completo" },
    { id: "preferencias", label: "Preferências", icon: SlidersHorizontal },
  ];

  const controleLocked = !hasAccess(userPlan, "completo");

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-card">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500 text-white">
            <Settings className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Sistema</h1>
            <p className="text-sm text-muted-foreground">Configurações gerais do sistema</p>
          </div>
        </div>
      </div>

      {/* Sub-abas — centralizadas. `overflow-x-auto` + `justify-start` em telas
          estreitas: centralizar quando NÃO cabe cortaria as abas das pontas e
          esconderia a primeira; a partir de sm centraliza de verdade. */}
      <div className="border-b bg-card px-4">
        {/* scrollbar-hide: a barra de rolagem aparecia como um 2º traço cinza sob "Impressão" */}
        <div className="flex gap-1 overflow-x-auto scrollbar-hide justify-start sm:justify-center">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            const tabLocked = tab.requiredPlan && !hasAccess(userPlan, tab.requiredPlan);
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tabLocked) {
                    toast({
                      title: "Recurso bloqueado",
                      description: `Disponível no plano ${getPlanLabel(tab.requiredPlan)}`,
                    });
                    return;
                  }
                  setActiveSubTab(tab.id);
                }}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeSubTab === tab.id
                    ? "border-purple-500 text-purple-600"
                    : tabLocked
                      ? "border-transparent text-muted-foreground/50 cursor-not-allowed"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                title={tabLocked ? `Disponível no plano ${getPlanLabel(tab.requiredPlan)}` : tab.label}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span className={tabLocked ? "line-through opacity-70" : ""}>{tab.label}</span>
                  {tabLocked && <PlanBadge requiredPlan={tab.requiredPlan} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto p-6">
        {activeSubTab === "impressao" && <ImpressaoTab toast={toast} />}
        {activeSubTab === "aparencia" && <AparenciaTab toast={toast} />}
        {activeSubTab === "integracao" && <IntegracaoTab toast={toast} />}
        {/* Aba "bot" removida — persona/contexto agora em Chatbot → Contexto */}
        {activeSubTab === "empresa" && <EmpresaTab toast={toast} />}
        {activeSubTab === "preferencias" && <PreferenciasTab toast={toast} />}
        {activeSubTab === "controle" && (
          controleLocked
            ? <PlanGateOverlay requiredPlan="completo"><ControleTab toast={toast} /></PlanGateOverlay>
            : <ControleTab toast={toast} />
        )}
      </div>
    </div>
  );
}

// ==================== ABA IMPRESSÃO ====================
function ImpressaoTab({ toast }) {
  const [impressaoSubTab, setImpressaoSubTab] = useState("conector");

  const impressaoSubTabs = [
    { id: "conector", label: "Print Connector" },
    { id: "configurar", label: "Configurar impressão" },
    { id: "preparo", label: "Cupom de Preparo" },
    { id: "producao", label: "Cupom de Produção" },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Sub-abas */}
      <div className="flex gap-1 border-b border-border overflow-x-auto justify-start sm:justify-center">
        {impressaoSubTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setImpressaoSubTab(t.id)}
            className={`px-4 py-2.5 font-medium text-sm border-b-2 -mb-px transition-colors ${
              impressaoSubTab === t.id
                ? "border-slate-600 text-slate-700 dark:border-slate-400 dark:text-slate-200"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {impressaoSubTab === "conector" && <ConectorSubTab toast={toast} />}
      {impressaoSubTab === "configurar" && <ConfigurarImpressaoSubTab toast={toast} />}
      {impressaoSubTab === "preparo" && <PreviewCupomPreparo />}
      {impressaoSubTab === "producao" && <PreviewCupomProducao />}
    </div>
  );
}

// Valores especiais do select "Impressoras deste computador"
const SETOR_NAO_USAR = "__nao_usar__";  // grava {nao_usar: true} — o PC não imprime o setor
const SETOR_PADRAO = "__padrao__";      // sem config — o Núcleo Desktop usa a impressora padrão

// Módulos disponíveis para roteamento
const PRINT_MODULES = [
  { id: "Delivery",  label: "Delivery",         icon: MotoEntregaIcon,  color: "bg-orange-500" },
  { id: "Mesas",     label: "Mesas",            icon: MesaIcon,         color: "bg-blue-500" },
  { id: "Cardapio",  label: "Cardapio Digital",  icon: QrCode,           color: "bg-pink-500" },
  { id: "WhatsApp",  label: "WhatsApp",          icon: MessageCircle,    color: "bg-green-500" },
  { id: "Balcao",    label: "Balcao",            icon: CaixaRegistradoraIcon, color: "bg-purple-500" },
];

// Sub-aba Configurar Impressao — tamanho da fonte dos cupons
function ConfigurarImpressaoSubTab({ toast }) {
  const [fontScale, setFontScale] = useState(1.0);
  const [originalFontScale, setOriginalFontScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [company, setCompany] = useState({ company_name: 'NOME DA EMPRESA', slogan: 'slogan da empresa', logo_url: null });

  useEffect(() => {
    (async () => {
      try {
        const tok = localStorage.getItem("token");
        const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
        // Carrega config de impressao + dados da empresa em paralelo (pra mostrar logo no preview)
        const [impRes, compRes] = await Promise.all([
          fetch(`${API_URL}/api/settings/impressao`, { headers, cache: "no-store" }),
          fetch(`${API_URL}/api/company/settings`, { headers }).catch(() => null),
        ]);
        if (impRes && impRes.ok) {
          const data = await impRes.json();
          const fs = Number(data?.font_scale);
          const valid = Number.isFinite(fs) && fs > 0 ? fs : 1.0;
          setFontScale(valid);
          setOriginalFontScale(valid);
        }
        if (compRes && compRes.ok) {
          const c = await compRes.json();
          if (c && typeof c === 'object') setCompany(prev => ({ ...prev, ...c }));
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const tok = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/settings/impressao`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ font_scale: fontScale }),
      });
      if (!res.ok) throw new Error("Falha ao salvar");
      setOriginalFontScale(fontScale);
      // Invalida o cache local pra que o proximo cupom impresso ja use o novo valor
      // sem esperar o TTL de 60s expirar. Outros dispositivos do tenant vao pegar o
      // novo valor naturalmente apos o ciclo de cache.
      invalidateImpressaoCache();
      setJustSaved(true);
      setLastSavedAt(new Date());
      setTimeout(() => setJustSaved(false), 3000);
      const modoLabel = fontScale < 0.95 ? 'REDUZIDO (fonte condensada)' : 'PADRÃO';
      toast({ title: "✅ Configuração salva", description: `Cupons agora serão impressos no modo ${modoLabel}. Aplicado em todos os cupons do tenant (preparo, fechamento, delivery, caixa, tickets).` });
    } catch (e) {
      toast({ title: "Erro ao salvar", description: String(e.message || e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Modo binario: PADRAO mantem layout/tamanho original (template legado intacto).
  // REDUZIDO usa Fonte B condensada via ESC/POS no plain text — economiza ~30% de papel.
  const MODO_PADRAO_VAL = 1.0;
  const MODO_REDUZIDO_VAL = 0.7;
  const modos = [
    {
      id: 'padrao',
      label: 'Padrão',
      value: MODO_PADRAO_VAL,
      hint: 'Tamanho original — facilita leitura',
    },
    {
      id: 'reduzido',
      label: 'Reduzido',
      value: MODO_REDUZIDO_VAL,
      hint: 'Fonte condensada — economiza papel',
    },
  ];

  const isReduzido = fontScale < 0.95;
  const modoAtual = isReduzido ? 'reduzido' : 'padrao';
  const dirty = Math.abs(fontScale - originalFontScale) > 0.001;

  const [forcingUpdate, setForcingUpdate] = useState(false);
  const handleForceUpdate = async () => {
    setForcingUpdate(true);
    try {
      const tok = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/print/connector/force-update`, {
        method: "POST",
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.sent_to > 0) {
        toast({ title: "✅ Atualização disparada", description: `${data.sent_to} Print Connector(es) vão baixar a nova versão e reiniciar automaticamente.` });
      } else if (data?.status === "no_devices") {
        toast({ title: "Nenhum conector online", description: "Não há Print Connector conectado agora. A atualização será aplicada quando reabrir o app desktop.", variant: "destructive" });
      } else {
        throw new Error(data?.detail || "Falha desconhecida");
      }
    } catch (e) {
      toast({ title: "Erro", description: String(e.message || e), variant: "destructive" });
    } finally { setForcingUpdate(false); }
  };

  // Carregamento com cards (25/10/2026): nunca mostra "Padrão" marcado antes de
  // saber o modo gravado.
  if (loading) {
    return (
      <div className="space-y-6">
        <CarregandoBloco altura="h-40" />
        <CarregandoFormulario campos={2} />
        <CarregandoBloco altura="h-72" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="text-lg font-semibold">Tamanho da fonte dos cupons</h3>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
            <AlertTriangle className="w-3 h-3" />
            Beta
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Escolha entre o tamanho padrão (igual sempre foi) ou reduzido (fonte condensada, economiza ~30% de papel). Aplica em todos os cupons impressos.
        </p>
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            <span className="font-bold">Recurso em BETA:</span> o modo REDUZIDO depende da impressora suportar Fonte B (ESC/POS). A maioria das térmicas de 80mm suporta, mas algumas impressoras antigas podem ignorar e imprimir no tamanho normal. Teste após salvar.
          </p>
        </div>
      </div>

      {/* 2 modos: Padrao e Reduzido */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {modos.map((m) => {
          const ativo = modoAtual === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setFontScale(m.value)}
              className={`flex flex-col items-start gap-1 rounded-xl border-2 p-4 text-left transition-colors ${
                ativo
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/40 hover:bg-muted'
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`text-base font-bold uppercase tracking-wide ${ativo ? 'text-primary' : ''}`}>{m.label}</span>
                {ativo && <CheckCircle className="w-5 h-5 text-primary" />}
              </div>
              <span className="text-xs text-muted-foreground">{m.hint}</span>
            </button>
          );
        })}
      </div>

      {/* Preview comparativo — PADRAO maior (com logo), REDUZIDO menor */}
      <div className="space-y-2">
        <p className="text-sm font-semibold">Pré-visualização</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3 items-start">
          {(() => {
            const logoSrc = buildLogoUrl(company);
            const nomeEmp = (company.company_name || company.fantasy_name || 'NOME DA EMPRESA').toUpperCase();
            const slogan = company.slogan || 'slogan da empresa';
            // BASE fontes: PADRAO usa tamanho real do cupom (13/16/11/15). REDUZIDO ~70% — VISUAL exagerado pra evidenciar a diferenca.
            const previewPadrao = { fontBase: 16, fontHeader: 20, fontSlogan: 13, fontTotal: 18, logoSide: 64 };
            const previewReduzido = { fontBase: 11, fontHeader: 13, fontSlogan: 9, fontTotal: 12, logoSide: 0 };
            const ativoPadrao = !isReduzido;
            return (
              <>
                {/* PADRAO — maior, com logo */}
                <div className={`bg-white text-black rounded-lg border p-4 transition-all ${ativoPadrao ? 'border-primary ring-2 ring-primary/30 shadow-lg' : 'opacity-70'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 text-center">Padrão</p>
                  <div style={{ fontFamily: '"Courier New", monospace', fontSize: previewPadrao.fontBase, fontWeight: 'bold' }}>
                    {logoSrc && (
                      <div style={{ textAlign: 'center', marginBottom: 6 }}>
                        <img src={logoSrc} alt="logo" style={{ width: previewPadrao.logoSide, height: previewPadrao.logoSide, objectFit: 'cover', display: 'inline-block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                    <div style={{ textAlign: 'center', fontSize: previewPadrao.fontHeader }}>{nomeEmp}</div>
                    <div style={{ textAlign: 'center', fontSize: previewPadrao.fontSlogan, fontStyle: 'italic', color: '#333' }}>{slogan}</div>
                    <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }}></div>
                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>— FECHAMENTO MESA 01 —</div>
                    <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }}></div>
                    <div>1 x 30,00 Burger Bacon</div>
                    <div>2 x 6,00 Coca Cola</div>
                    <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: previewPadrao.fontTotal }}>
                      <span>TOTAL:</span><span>R$ 42,00</span>
                    </div>
                  </div>
                </div>

                {/* REDUZIDO — menor, sem logo */}
                <div className={`bg-white text-black rounded-lg border p-3 transition-all ${!ativoPadrao ? 'border-primary ring-2 ring-primary/30 shadow-lg' : 'opacity-70'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 text-center">Reduzido</p>
                  <div style={{ fontFamily: '"Courier New", monospace', fontSize: previewReduzido.fontBase, fontWeight: 'bold' }}>
                    <div style={{ textAlign: 'center', fontSize: previewReduzido.fontHeader }}>{nomeEmp}</div>
                    <div style={{ textAlign: 'center', fontSize: previewReduzido.fontSlogan, fontStyle: 'italic', color: '#333' }}>{slogan}</div>
                    <div style={{ borderBottom: '1px dashed #000', margin: '5px 0' }}></div>
                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>— FECHAMENTO MESA 01 —</div>
                    <div style={{ borderBottom: '1px dashed #000', margin: '5px 0' }}></div>
                    <div>1 x 30,00 Burger Bacon</div>
                    <div>2 x 6,00 Coca Cola</div>
                    <div style={{ borderBottom: '1px dashed #000', margin: '5px 0' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: previewReduzido.fontTotal }}>
                      <span>TOTAL:</span><span>R$ 42,00</span>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
        <p className="text-[11px] text-muted-foreground text-center">
          Aproximação visual. Na impressora térmica, o REDUZIDO usa a Fonte B (9×17) — caracteres mais finos.
        </p>
      </div>

      {/* Status linha — mostra ultima vez salva (persiste enquanto a aba estiver aberta) */}
      {lastSavedAt && (
        <div className="flex items-center justify-center gap-2 text-xs">
          <CheckCircle className={`w-4 h-4 ${justSaved ? 'text-green-500 animate-pulse' : 'text-green-600/70'}`} />
          <span className={justSaved ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
            {justSaved ? 'Salvo agora' : `Última alteração salva ${lastSavedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
            {' · '}
            <span className="font-semibold uppercase">{originalFontScale < 0.95 ? 'REDUZIDO' : 'PADRÃO'}</span>
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        {!lastSavedAt && !dirty && (
          <span className="text-xs text-muted-foreground mr-auto">
            Configuração atual: <span className="font-semibold uppercase">{originalFontScale < 0.95 ? 'REDUZIDO' : 'PADRÃO'}</span>
          </span>
        )}
        {dirty && (
          <Button variant="outline" onClick={() => setFontScale(originalFontScale)} disabled={saving}>
            Cancelar
          </Button>
        )}
        <Button onClick={handleSave} disabled={!dirty || saving} className={justSaved ? 'bg-green-600 hover:bg-green-700' : ''}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : justSaved ? <CheckCircle className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {justSaved ? 'Salvo!' : 'Salvar'}
        </Button>
      </div>

      {/* Bloco de atualizacao dos Print Connectors */}
      <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Sincronizar Print Connectors</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Se a fonte ainda não mudou nos cupons, os apps desktop precisam atualizar para uma versão nova que suporta a configuração.
              Clique abaixo pra disparar a atualização imediata em todos os conectores deste estabelecimento (sem esperar o ciclo automático de 30 min).
            </p>
            <Button size="sm" variant="outline" className="mt-3" onClick={handleForceUpdate} disabled={forcingUpdate}>
              {forcingUpdate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Atualizar conectores agora
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Sub-aba Print Connector (Multi-Conector)
function ConectorSubTab({ toast }) {
  const isElectron = !!window.nucleoDesktop;
  const [electronPrintStatus, setElectronPrintStatus] = useState(null);
  const [electronPrinters, setElectronPrinters] = useState([]);
  const [testingPrinter, setTestingPrinter] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Routing por modulo (pedidos por computador) ──
  // Estado: lista de PCs conhecidos + mapa modulo->[device_ids]. Permite marcar varios PCs por modulo (redundancia).
  const [connectors, setConnectors] = useState([]);  // [{device_id, hostname, display_name, is_online, ...}]
  const [moduleRouting, setModuleRouting] = useState({});  // {Mesas: [...ids], Delivery: [...]}
  const [originalRouting, setOriginalRouting] = useState({});
  const [moduleOriginLock, setModuleOriginLock] = useState({});  // {Balcao: true} — imprime so no PC que criou
  const [originalOriginLock, setOriginalOriginLock] = useState({});
  const [loadingRouting, setLoadingRouting] = useState(true);
  const [savingRouting, setSavingRouting] = useState(false);
  const ALL_MODULES = [
    { key: "Mesas", label: "Mesas", icon: "🍽️", color: "bg-blue-500" },
    { key: "Comandas", label: "Comandas", icon: "📋", color: "bg-amber-500" },
    { key: "Delivery", label: "Delivery", icon: "🛵", color: "bg-orange-500" },
    { key: "Balcao", label: "Balcão", icon: "🏪", color: "bg-purple-500" },
    { key: "Cardapio", label: "Cardápio", icon: "📱", color: "bg-pink-500" },
  ];

  const fetchRoutingAndConnectors = async () => {
    try {
      const tok = localStorage.getItem("token");
      const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
      const [connsRes, routingRes, lockRes] = await Promise.all([
        fetch(`${API_URL}/api/print/connectors`, { headers }),
        fetch(`${API_URL}/api/print/module-routing`, { headers }),
        fetch(`${API_URL}/api/print/module-origin-lock`, { headers }).catch(() => null),
      ]);
      if (connsRes.ok) {
        const conns = await connsRes.json();
        setConnectors(Array.isArray(conns) ? conns : []);
      }
      if (routingRes.ok) {
        const r = await routingRes.json();
        const norm = r && typeof r === 'object' ? r : {};
        setModuleRouting(norm);
        setOriginalRouting(norm);
      }
      if (lockRes && lockRes.ok) {
        const l = await lockRes.json();
        const norm = l && typeof l === 'object' ? l : {};
        setModuleOriginLock(norm);
        setOriginalOriginLock(norm);
      }
    } catch (e) {
      console.error("[ROUTING] Erro ao buscar:", e);
    } finally {
      setLoadingRouting(false);
    }
  };

  const toggleOriginLockForModule = (moduleKey) => {
    setModuleOriginLock(prev => ({ ...prev, [moduleKey]: !prev[moduleKey] }));
  };

  // ── Device sector printers (impressoras de OUTROS PCs por setor) ──
  const [allPrintersByDevice, setAllPrintersByDevice] = useState([]);  // [{device_id, hostname, is_online, printers:[]}]
  const [deviceSectorPrinters, setDeviceSectorPrinters] = useState({});  // {device_id_origem: {cozinha: {device_id, printer}}}
  const [originalDeviceSectorPrinters, setOriginalDeviceSectorPrinters] = useState({});
  const [savingSectorPrinters, setSavingSectorPrinters] = useState(false);

  const fetchAllPrinters = async () => {
    try {
      const tok = localStorage.getItem("token");
      const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
      const [allPrintersRes, dspRes] = await Promise.all([
        fetch(`${API_URL}/api/print/all-printers`, { headers }),
        fetch(`${API_URL}/api/print/device-sector-printers`, { headers }),
      ]);
      if (allPrintersRes.ok) {
        const data = await allPrintersRes.json();
        setAllPrintersByDevice(Array.isArray(data) ? data : []);
      }
      if (dspRes.ok) {
        const data = await dspRes.json();
        const norm = data && typeof data === 'object' ? data : {};
        setDeviceSectorPrinters(norm);
        setOriginalDeviceSectorPrinters(norm);
      }
    } catch (e) { console.error("[ALL-PRINTERS] erro:", e); }
  };

  // Salva a config de sectorPrinters do PC ATUAL via backend (substitui o set local do Electron quando aponta pra outro PC)
  // SETOR_NAO_USAR grava {nao_usar: true}: o servidor não manda o cupom desse setor pra este PC.
  // Sem entrada = impressora padrão do computador (o Núcleo Desktop decide).
  const setSectorPrinterRemote = (sector, targetDeviceId, printerName) => {
    if (!electronPrintStatus?.deviceId) return;
    const myId = electronPrintStatus.deviceId;
    setDeviceSectorPrinters(prev => {
      const myMap = { ...(prev[myId] || {}) };
      if (targetDeviceId === SETOR_NAO_USAR) {
        myMap[sector] = { nao_usar: true };
      } else if (!targetDeviceId || !printerName) {
        delete myMap[sector];
      } else {
        myMap[sector] = { device_id: targetDeviceId, printer: printerName };
      }
      return { ...prev, [myId]: myMap };
    });
  };

  const saveSectorPrinters = async () => {
    setSavingSectorPrinters(true);
    try {
      const tok = localStorage.getItem("token");
      // Tambem aplica LOCAL no Electron quando aponta pra impressora deste PC (mantem compatibilidade)
      const myId = electronPrintStatus?.deviceId;
      if (isElectron && myId) {
        const myMap = deviceSectorPrinters[myId] || {};
        for (const sector of Object.keys(myMap)) {
          const cfg = myMap[sector];
          if (cfg && cfg.device_id === myId && window.nucleoDesktop?.setSectorPrinter) {
            try { await window.nucleoDesktop.setSectorPrinter(sector, cfg.printer); } catch (_) {}
          }
        }
      }
      const res = await fetch(`${API_URL}/api/print/device-sector-printers`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify(deviceSectorPrinters),
      });
      if (!res.ok) throw new Error("Falha ao salvar");
      setOriginalDeviceSectorPrinters(deviceSectorPrinters);
      toast({ title: "✅ Salvo", description: "Impressoras por setor atualizadas." });
      fetchElectronPrintData();
    } catch (e) {
      toast({ title: "Erro", description: String(e.message || e), variant: "destructive" });
    } finally { setSavingSectorPrinters(false); }
  };

  const sectorPrintersDirty = JSON.stringify(deviceSectorPrinters || {}) !== JSON.stringify(originalDeviceSectorPrinters || {});

  const toggleModuleForDevice = (deviceId, moduleKey) => {
    setModuleRouting(prev => {
      const current = Array.isArray(prev[moduleKey]) ? prev[moduleKey] : [];
      const isOn = current.includes(deviceId);
      const updated = isOn ? current.filter(id => id !== deviceId) : [...current, deviceId];
      return { ...prev, [moduleKey]: updated };
    });
  };

  const routingDirty =
    JSON.stringify(moduleRouting || {}) !== JSON.stringify(originalRouting || {}) ||
    JSON.stringify(moduleOriginLock || {}) !== JSON.stringify(originalOriginLock || {});

  // Estado pra accordion da lista de impressoras (default fechado)
  const [printerListOpen, setPrinterListOpen] = useState(false);

  // Helper: "Offline ha 2h" / "Offline ha 3 dias" / "Offline ha 5 min"
  const formatLastSeen = (iso) => {
    if (!iso) return 'Offline';
    try {
      const last = new Date(iso);
      const diffMs = Date.now() - last.getTime();
      if (diffMs < 60_000) return 'Offline agora';
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 60) return `Offline há ${mins} min`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `Offline há ${hours}h`;
      const days = Math.floor(hours / 24);
      return `Offline há ${days} dia${days > 1 ? 's' : ''}`;
    } catch (_) { return 'Offline'; }
  };

  const saveRouting = async () => {
    setSavingRouting(true);
    try {
      const tok = localStorage.getItem("token");
      const headers = { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
      // Normaliza routing: remove modulos com array vazio
      const cleanRouting = {};
      for (const k of Object.keys(moduleRouting || {})) {
        if (Array.isArray(moduleRouting[k]) && moduleRouting[k].length > 0) cleanRouting[k] = moduleRouting[k];
      }
      // Normaliza origin lock: so envia keys com true
      const cleanLock = {};
      for (const k of Object.keys(moduleOriginLock || {})) {
        if (moduleOriginLock[k] === true) cleanLock[k] = true;
      }
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/print/module-routing`, { method: "PUT", headers, body: JSON.stringify(cleanRouting) }),
        fetch(`${API_URL}/api/print/module-origin-lock`, { method: "PUT", headers, body: JSON.stringify(cleanLock) }),
      ]);
      if (!r1.ok || !r2.ok) throw new Error("Falha ao salvar");
      setOriginalRouting(cleanRouting);
      setModuleRouting(cleanRouting);
      setOriginalOriginLock(cleanLock);
      setModuleOriginLock(cleanLock);
      toast({ title: "✅ Salvo", description: "Roteamento e origem por módulo atualizados." });
    } catch (e) {
      toast({ title: "Erro", description: String(e.message || e), variant: "destructive" });
    } finally {
      setSavingRouting(false);
    }
  };

  // Buscar status e impressoras do Electron
  const fetchElectronPrintData = async () => {
    if (!isElectron) return;
    setLoading(true);
    try {
      const [status, printers] = await Promise.all([
        window.nucleoDesktop.getPrintStatus(),
        window.nucleoDesktop.getPrinters()
      ]);
      console.log('[PRINT-UI] Status:', status);
      console.log('[PRINT-UI] Impressoras:', printers);
      setElectronPrintStatus(status);
      setElectronPrinters(printers || []);
    } catch (e) {
      console.error("[PRINT-UI] Erro ao buscar dados:", e);
    } finally { setLoading(false); }
  };

  // Configurar impressora de setor
  const setSectorPrinter = async (sector, printerName) => {
    try {
      await window.nucleoDesktop.setSectorPrinter(sector, printerName);
      toast({ title: "Sucesso!", description: `${sector.toUpperCase()} → ${printerName}` });
      fetchElectronPrintData();
    } catch (e) {
      toast({ title: "Erro", description: e.message, variant: "destructive" });
    }
  };

  // Teste de impressão
  const testPrint = async (printerName, type) => {
    console.log('[PRINT-UI] Teste:', type, 'para:', printerName);
    setTestingPrinter(`${printerName}-${type}`);
    try {
      const result = await window.nucleoDesktop.testPrint(printerName, type);
      if (result.success) {
        toast({ title: "Imprimindo!", description: `${type === 'entrega' ? 'Cupom de entrega' : 'Cupom de preparo'} enviado para ${printerName}` });
      } else {
        toast({ title: "Erro", description: result.error || "Falha no teste", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Erro", description: e.message, variant: "destructive" });
    } finally { setTestingPrinter(null); }
  };

  // Teste de impressao em PC REMOTO via backend (usa endpoint /api/print/test/{device_id}?printer=NOME)
  // Remove um conector (somente offline — online ele reaparece no proximo heartbeat).
  // Backend faz cascata: tira do routing, setores centralizados e device_sector_printers.
  const deletePrintConnector = async (deviceId, displayName) => {
    if (!window.confirm(`Remover "${displayName}" da lista?\n\nIsso vai limpar as configurações desse PC (módulos atribuídos, redirecionamento de impressora). Se ele voltar a conectar no futuro, vai aparecer de novo como PC novo.`)) return;
    try {
      const tok = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/print/connector/${encodeURIComponent(deviceId)}`, {
        method: 'DELETE',
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (res.ok) {
        toast({ title: "PC removido", description: `${displayName} foi removido da lista.` });
        fetchRoutingAndConnectors();
        fetchAllPrinters();
      } else {
        const data = await res.json().catch(() => ({}));
        toast({ title: "Erro ao remover", description: data?.detail || "Tente de novo", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Erro", description: String(e.message || e), variant: "destructive" });
    }
  };

  const testPrintRemote = async (deviceId, printerName, type, hostnameLabel) => {
    const route = type === 'preparo' ? 'COZINHA' : 'CAIXA';
    setTestingPrinter(`remote-${deviceId}-${printerName}-${type}`);
    console.log(`[TEST-REMOTE] >>> Iniciando: device=${deviceId}, printer="${printerName}", route=${route}, host=${hostnameLabel}`);
    try {
      const tok = localStorage.getItem("token");
      const qs = new URLSearchParams({ printer: printerName, route }).toString();
      const url = `${API_URL}/api/print/test/${encodeURIComponent(deviceId)}?${qs}`;
      console.log(`[TEST-REMOTE] POST ${url}`);
      const t0 = Date.now();
      const res = await fetch(url, {
        method: 'POST',
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const elapsed = Date.now() - t0;
      const data = await res.json().catch(() => ({}));
      console.log(`[TEST-REMOTE] <<< status=${res.status} em ${elapsed}ms | body=`, data);
      if (res.ok) {
        // status: 'ok' -> impresso confirmado | status: 'pending' -> WS chegou mas sem confirmacao no prazo
        if (data?.status === 'pending') {
          toast({
            title: "Sem confirmação",
            description: data.message || `Comando foi pra ${hostnameLabel} mas o PC remoto não confirmou em 8s. Cheque a impressora.`,
          });
        } else {
          toast({
            title: "Imprimiu!",
            description: `${printerName} em ${hostnameLabel} confirmou a impressão.`,
          });
        }
      } else if (res.status === 503) {
        toast({
          title: "PC remoto offline",
          description: `${hostnameLabel} não está conectado. Não foi possível imprimir.`,
          variant: "destructive",
        });
      } else if (res.status === 409) {
        // PC remoto numa versao antiga que nao entende o override de impressora.
        toast({
          title: "PC remoto precisa atualizar",
          description: data?.detail || `${hostnameLabel} esta numa versao do Nucleo Desktop muito antiga. Feche e reabra o app la pra acionar o auto-update.`,
          variant: "destructive",
        });
      } else if (res.status === 502) {
        // PC remoto recebeu mas a impressao falhou. data.detail traz o erro real
        // do printer-manager do Electron (ex: "impressora 'pdv' nao encontrada",
        // "driver retornou erro", etc).
        toast({
          title: "Falha na impressão remota",
          description: data?.detail || `${hostnameLabel} recebeu o comando mas a impressora falhou.`,
          variant: "destructive",
        });
      } else if (res.status === 504) {
        // WS zumbi: backend acha que o PC remoto ta conectado mas ele nao responde.
        toast({
          title: "Conexão zumbi",
          description: data?.detail || `${hostnameLabel} aparece online mas não responde. Reabra o Núcleo Desktop lá.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "Erro", description: data?.detail || "Falha no teste", variant: "destructive" });
      }
    } catch (e) {
      console.error(`[TEST-REMOTE] !!! Exception:`, e);
      toast({ title: "Erro", description: String(e.message || e), variant: "destructive" });
    } finally { setTestingPrinter(null); }
  };

  useEffect(() => {
    if (isElectron) fetchElectronPrintData();
    const interval = setInterval(() => { if (isElectron) fetchElectronPrintData(); }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Carrega routing + lista de conectores (mesmo quando nao for Electron, pra admin web ver)
  useEffect(() => {
    fetchRoutingAndConnectors();
    fetchAllPrinters();
    const t = setInterval(() => {
      fetchRoutingAndConnectors();
      fetchAllPrinters();
    }, 30000);  // refresh online status + impressoras a cada 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se não é Electron, mostrar mensagem para usar o app desktop
  if (!isElectron) {
    return (
      <div className="space-y-6">
        <AvisoPreparoDesligado />
        <div className="bg-card border rounded-xl p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
            <Monitor className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Impressão via Núcleo Desktop</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto mb-4">
            Para configurar impressoras e imprimir cupons, utilize o aplicativo Núcleo Desktop.
            A impressão funciona de forma integrada e automática.
          </p>
          <Button asChild>
            <a href="https://usenucleo.com.br/downloads/Nucleo-Setup.exe" download>
              <Download className="w-4 h-4 mr-2" />
              Baixar Núcleo Desktop
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AvisoPreparoDesligado />
      {/* Header + Status compactos numa linha só */}
      <div className="bg-card border rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${electronPrintStatus?.wsConnected ? "bg-green-500 animate-pulse" : "bg-amber-500"}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">
              {electronPrintStatus?.wsConnected ? 'Conectado ao servidor' : 'Conectando...'}
            </p>
            {electronPrintStatus?.deviceId && (
              <p className="text-[10px] text-muted-foreground truncate">Este PC: {electronPrintStatus.deviceId}</p>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchElectronPrintData} disabled={loading} className="h-8 shrink-0">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          BLOCO 1 — ROTEAMENTO: Quem imprime cada tipo de pedido
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-card border-2 border-primary/20 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b bg-primary/5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Monitor className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h4 className="font-bold text-base">Quem imprime cada tipo de pedido?</h4>
              <p className="text-[11px] text-muted-foreground">Marque quais módulos cada computador deve imprimir. Vários PCs podem imprimir o mesmo módulo.</p>
            </div>
          </div>
          {routingDirty && (
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setModuleRouting(originalRouting)} disabled={savingRouting}>
                Cancelar
              </Button>
              <Button size="sm" onClick={saveRouting} disabled={savingRouting} className="bg-primary">
                {savingRouting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Salvar
              </Button>
            </div>
          )}
        </div>

        {loadingRouting ? (
          <CarregandoTabela linhas={2} colunas={6} className="" />
        ) : connectors.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Monitor className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" />
            <p className="text-sm font-medium mb-1">Nenhum computador conectado ainda</p>
            <p className="text-xs text-muted-foreground">Abra o Núcleo Desktop em pelo menos 1 PC pra começar a configurar.</p>
          </div>
        ) : (
          <>
            {/* Tabela matriz: linhas = PCs, colunas = módulos */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-muted-foreground py-2.5 px-3 sticky left-0 bg-muted/30 z-10">Computador</th>
                    {ALL_MODULES.map(mod => {
                      const locked = moduleOriginLock[mod.key] === true;
                      return (
                        <th key={mod.key} className="text-center font-semibold text-xs uppercase tracking-wide text-muted-foreground py-2.5 px-2 min-w-[110px]">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-base leading-none">{mod.icon}</span>
                            <span>{mod.label}</span>
                            <button
                              type="button"
                              onClick={() => toggleOriginLockForModule(mod.key)}
                              title={locked
                                ? `Pedidos de ${mod.label} imprimem SÓ no PC que criou a venda. Clique pra desativar.`
                                : `Ativar: pedidos de ${mod.label} imprimem só no PC de origem (ignora os checks abaixo).`
                              }
                              className={`mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider transition-colors ${
                                locked
                                  ? 'bg-amber-500 text-white border border-amber-500 hover:bg-amber-600'
                                  : 'bg-transparent text-muted-foreground border border-dashed border-muted-foreground/40 hover:border-amber-500/60 hover:text-amber-600'
                              }`}
                            >
                              📍 {locked ? 'No PC origem' : 'PC origem off'}
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Ordena: ESTE PC primeiro, depois online (por hostname), depois offline (por hostname)
                    const thisId = electronPrintStatus?.deviceId;
                    return [...connectors].sort((a, b) => {
                      if (thisId && a.device_id === thisId) return -1;
                      if (thisId && b.device_id === thisId) return 1;
                      if ((a.is_online ? 1 : 0) !== (b.is_online ? 1 : 0)) return b.is_online - a.is_online;
                      return (a.hostname || a.device_id).localeCompare(b.hostname || b.device_id);
                    });
                  })().map((conn, idx) => {
                    const isThisPC = isElectron && electronPrintStatus?.deviceId === conn.device_id;
                    const displayName = conn.display_name || conn.hostname || conn.device_id;
                    const statusLabel = conn.is_online ? 'Online' : formatLastSeen(conn.last_seen_at);
                    return (
                      <tr key={conn.device_id} className={`border-b last:border-b-0 ${isThisPC ? 'bg-primary/5' : (idx % 2 ? 'bg-muted/10' : '')}`}>
                        <td className="py-3 px-3 sticky left-0 z-10" style={{ background: isThisPC ? 'var(--primary-5, rgba(0,0,0,0.02))' : (idx % 2 ? 'var(--muted-10, transparent)' : 'inherit') }}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${conn.is_online ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-semibold text-sm truncate">{displayName}</p>
                                {isThisPC && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground font-bold uppercase tracking-wide leading-none">
                                    Este PC
                                  </span>
                                )}
                              </div>
                              <p className={`text-[10px] ${conn.is_online ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>{statusLabel}</p>
                            </div>
                            {/* Botao remover: aparece so em PCs OFFLINE (online voltariam no proximo heartbeat). Auto-dedup no backend ja
                                limpa offline com mesmo hostname quando novo PC sobe, mas mantemos a remocao manual pra casos especiais. */}
                            {!conn.is_online && !isThisPC && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); deletePrintConnector(conn.device_id, displayName); }}
                                className="shrink-0 w-7 h-7 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors inline-flex items-center justify-center"
                                title={`Remover ${displayName} da lista`}
                                aria-label={`Remover ${displayName}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        {ALL_MODULES.map(mod => {
                          const selected = Array.isArray(moduleRouting[mod.key]) && moduleRouting[mod.key].includes(conn.device_id);
                          const lockedByOrigin = moduleOriginLock[mod.key] === true;
                          return (
                            <td key={mod.key} className="text-center py-3 px-2">
                              <button
                                type="button"
                                onClick={() => toggleModuleForDevice(conn.device_id, mod.key)}
                                disabled={lockedByOrigin}
                                aria-label={`${selected ? 'Desmarcar' : 'Marcar'} ${mod.label} para ${displayName}`}
                                title={lockedByOrigin ? `${mod.label} usa modo 'PC de origem' — esta config é ignorada` : ''}
                                className={`w-9 h-9 rounded-lg border-2 transition-all inline-flex items-center justify-center ${
                                  lockedByOrigin
                                    ? 'border-dashed border-amber-500/30 bg-amber-500/5 text-amber-500/40 cursor-not-allowed'
                                    : selected
                                      ? 'border-primary bg-primary text-primary-foreground shadow-md hover:bg-primary/90'
                                      : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5'
                                }`}
                              >
                                {lockedByOrigin ? <span className="text-xs">📍</span> : (selected && <Check className="w-5 h-5" strokeWidth={3} />)}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Resumo do status por modulo */}
            <div className="border-t bg-muted/20 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Status do roteamento</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ALL_MODULES.map(mod => {
                  const lockedByOrigin = moduleOriginLock[mod.key] === true;
                  const ids = Array.isArray(moduleRouting[mod.key]) ? moduleRouting[mod.key] : [];
                  const onlineCount = ids.filter(id => connectors.find(c => c.device_id === id && c.is_online)).length;
                  const totalCount = ids.length;
                  let statusText, statusColor;
                  if (lockedByOrigin) { statusText = '📍 Imprime no PC que criou a venda'; statusColor = 'text-amber-600 dark:text-amber-400'; }
                  else if (totalCount === 0) { statusText = 'Sem destino · usa fallback'; statusColor = 'text-amber-600 dark:text-amber-400'; }
                  else if (onlineCount === 0) { statusText = `${totalCount} PC offline · usa fallback`; statusColor = 'text-red-600 dark:text-red-400'; }
                  else { statusText = `${onlineCount}/${totalCount} PC${totalCount > 1 ? 's' : ''} online`; statusColor = 'text-green-600 dark:text-green-400'; }
                  return (
                    <div key={mod.key} className="flex items-center gap-2 text-xs">
                      <span className="text-base leading-none">{mod.icon}</span>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{mod.label}</p>
                        <p className={`text-[11px] ${statusColor}`}>{statusText}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          BLOCO 2 — IMPRESSORAS DESTE COMPUTADOR (com suporte a impressora em outros PCs)
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h4 className="font-bold text-base flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              Impressoras deste computador
            </h4>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Quando este PC (<span className="font-semibold">{electronPrintStatus?.deviceId || 'sem ID'}</span>) for imprimir, qual impressora usar? Pode escolher uma deste PC ou de outro PC do Núcleo conectado.
            </p>
          </div>
          {sectorPrintersDirty && (
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setDeviceSectorPrinters(originalDeviceSectorPrinters)} disabled={savingSectorPrinters}>Cancelar</Button>
              <Button size="sm" onClick={saveSectorPrinters} disabled={savingSectorPrinters}>
                {savingSectorPrinters ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Salvar
              </Button>
            </div>
          )}
        </div>
        <div className="p-4 space-y-3">
          {[
            { sector: 'cozinha', label: 'Cupom de Preparo', sublabel: 'Vai pra cozinha', icon: '👨‍🍳' },
            { sector: 'caixa', label: 'Cupom para Cliente', sublabel: 'Caixa, Mesa, Delivery, Balcão', icon: '💵' },
            { sector: 'ifood', label: 'iFood (opcional)', sublabel: 'Separa do caixa só se quiser', icon: '🛵' },
          ].map(({ sector, label, sublabel, icon }) => {
            const myId = electronPrintStatus?.deviceId;
            const myCfg = (deviceSectorPrinters[myId] || {})[sector];
            // iFood mantém o "Não usar" antigo (= sai junto com o caixa); preparo e
            // caixa têm "padrão do computador" e "Não usar" de verdade (não imprime).
            const temNaoUsar = sector !== 'ifood';
            // Valor combinado: "device_id|printer" pra select unico
            const currentValue = myCfg?.nao_usar && temNaoUsar
              ? SETOR_NAO_USAR
              : myCfg && myCfg.device_id && myCfg.printer
              ? `${myCfg.device_id}|${myCfg.printer}`
              : (electronPrintStatus?.sectorPrinters?.[sector]?.name ? `${myId}|${electronPrintStatus.sectorPrinters[sector].name}` : (temNaoUsar ? SETOR_PADRAO : ""));
            const isRemote = myCfg && myCfg.device_id && myCfg.device_id !== myId;
            return (
              <div key={sector} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isRemote ? 'bg-blue-500/5 border-blue-500/30' : 'bg-muted/20 hover:bg-muted/30'}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl shrink-0 bg-muted">
                  {icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {label}
                    {isRemote && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500 text-white font-bold uppercase tracking-wide leading-none">
                        Impressora remota
                      </span>
                    )}
                  </p>
                  {currentValue === SETOR_NAO_USAR
                    ? <p className="text-[11px] font-semibold text-red-500 truncate">Não imprime neste computador</p>
                    : <p className="text-[11px] text-muted-foreground truncate">{sublabel}</p>}
                </div>
                <select
                  className="border rounded-md px-2 py-1.5 text-xs bg-background min-w-[220px] max-w-[260px]"
                  value={currentValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === SETOR_NAO_USAR) {
                      setSectorPrinterRemote(sector, SETOR_NAO_USAR);
                      return;
                    }
                    if (!val || val === SETOR_PADRAO) {
                      setSectorPrinterRemote(sector, null, null);
                      return;
                    }
                    const [devId, ...rest] = val.split('|');
                    const printerName = rest.join('|');
                    setSectorPrinterRemote(sector, devId, printerName);
                  }}
                >
                  {temNaoUsar ? (
                    <>
                      <option value={SETOR_PADRAO}>Impressora padrão do computador</option>
                      <option value={SETOR_NAO_USAR}>— Não usar (não imprime) —</option>
                    </>
                  ) : (
                    <option value="">— Não usar —</option>
                  )}
                  {allPrintersByDevice.map((dev) => {
                    if (!dev.printers || dev.printers.length === 0) return null;
                    const isMe = dev.device_id === myId;
                    // Aviso: feature de impressao remota precisa de v1.0.32+ no PC ALVO.
                    // PCs antigos ignoram o override e mandam pra defaultPrinter (sintoma:
                    // "cliquei em testar e nada imprimiu na impressora certa").
                    const av = dev.app_version || '';
                    let tooOldForRemote = false;
                    if (!isMe && av) {
                      const parts = av.split('.').map(n => parseInt(n, 10) || 0);
                      while (parts.length < 3) parts.push(0);
                      tooOldForRemote = parts[0] < 1 || (parts[0] === 1 && (parts[1] < 0 || (parts[1] === 0 && parts[2] < 32)));
                    }
                    const versionTag = av ? ` v${av}` : '';
                    const offlineTag = (!isMe && !dev.is_online) ? ' (offline)' : '';
                    const oldTag = tooOldForRemote ? ' — atualize p/ v1.0.32+' : '';
                    const devLabel = isMe
                      ? '🖥️ Este computador'
                      : `🖥️ ${dev.hostname || dev.device_id}${versionTag}${offlineTag}${oldTag}`;
                    return (
                      <optgroup key={dev.device_id} label={devLabel}>
                        {dev.printers.map((p) => {
                          const pname = typeof p === 'string' ? p : (p?.name || '');
                          if (!pname) return null;
                          return (
                            <option
                              key={`${dev.device_id}|${pname}`}
                              value={`${dev.device_id}|${pname}`}
                              disabled={tooOldForRemote}
                            >
                              {pname}
                            </option>
                          );
                        })}
                      </optgroup>
                    );
                  })}
                </select>
                {currentValue.includes('|') && (() => {
                  const printerName = currentValue.split('|').slice(1).join('|');
                  const type = sector === 'cozinha' ? 'preparo' : 'entrega';
                  const isRemoteBtn = isRemote;
                  // Busca status do PC remoto em DUAS fontes (mais robusto a timing/cache):
                  // 1) allPrintersByDevice (vem de /api/print/all-printers — pode tar atrasado)
                  // 2) connectors (vem de /api/print/connectors — fonte ja usada na tabela)
                  const remoteFromPrinters = isRemoteBtn ? allPrintersByDevice.find(d => d.device_id === myCfg?.device_id) : null;
                  const remoteFromConn = isRemoteBtn ? connectors.find(c => c.device_id === myCfg?.device_id) : null;
                  const remoteHost = isRemoteBtn
                    ? (remoteFromPrinters?.hostname || remoteFromConn?.hostname || 'PC remoto')
                    : null;
                  // Considera online se QUALQUER uma das duas fontes diz online (truthy).
                  // Se ambas dizem offline → desabilita. Se nao encontrar em nenhuma → permite tentar.
                  const onlineFromPrinters = remoteFromPrinters ? !!remoteFromPrinters.is_online : null;
                  const onlineFromConn = remoteFromConn ? !!remoteFromConn.is_online : null;
                  const remoteOnline = !isRemoteBtn ? true
                    : (onlineFromPrinters === true || onlineFromConn === true ? true
                       : (onlineFromPrinters === null && onlineFromConn === null ? true  // nao encontrou → permite tentar
                          : false));
                  const testingId = isRemoteBtn
                    ? `remote-${myCfg.device_id}-${printerName}-${type}`
                    : `${printerName}-${type}`;
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (isRemoteBtn) {
                          testPrintRemote(myCfg.device_id, printerName, type, remoteHost);
                        } else {
                          testPrint(printerName, type);
                        }
                      }}
                      disabled={!!testingPrinter || (isRemoteBtn && !remoteOnline)}
                      className="shrink-0 h-8 text-xs"
                      title={isRemoteBtn
                        ? (remoteOnline ? `Imprimir teste na impressora ${printerName} em ${remoteHost}` : `${remoteHost} offline — não dá pra testar`)
                        : 'Imprimir teste'
                      }
                    >
                      {testingPrinter === testingId
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Play className="w-3.5 h-3.5" />}
                    </Button>
                  );
                })()}
              </div>
            );
          })}
        </div>

          {/* Accordion: lista de todas impressoras detectadas */}
          <button
            type="button"
            onClick={() => setPrinterListOpen(v => !v)}
            className="w-full px-4 py-2.5 border-t flex items-center justify-between text-xs text-muted-foreground hover:bg-muted/30 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              {printerListOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Ver as {electronPrinters.length} impressoras detectadas
            </span>
          </button>
          {printerListOpen && (
            <div className="border-t divide-y">
              {electronPrinters.map((printer) => {
                const configuredSectors = Object.entries(electronPrintStatus?.sectorPrinters || {})
                  .filter(([, sp]) => sp?.name === printer)
                  .map(([s]) => s === "ifood" ? "iFOOD" : s === "cozinha" ? "PREPARO" : "CAIXA");
                return (
                  <div key={printer} className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <Printer className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs font-medium truncate block">{printer}</span>
                        {configuredSectors.length > 0 && (
                          <p className="text-[10px] text-green-600 dark:text-green-400 font-semibold truncate">✓ {configuredSectors.join(" + ")}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => testPrint(printer, 'preparo')} disabled={!!testingPrinter} className="text-[10px] h-7 px-2">
                        {testingPrinter === `${printer}-preparo` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Preparo'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => testPrint(printer, 'entrega')} disabled={!!testingPrinter} className="text-[10px] h-7 px-2">
                        {testingPrinter === `${printer}-entrega` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Entrega'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}

// Preview do Cupom de Preparo (estilo cupomPreparo.js — cozinha)
function PreviewCupomPreparo() {
  const receiptStyle = {
    width: 280,
    minHeight: 420,
    fontFamily: '"Courier New", monospace',
    fontSize: 13,
    fontWeight: 'bold',
    background: '#fff',
    color: '#000',
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Visualização de como o cupom de preparo será impresso (cozinha).</p>
      <div className="flex justify-center p-6 bg-slate-100 dark:bg-slate-900/50 rounded-xl">
        <div
          className="shadow-lg border border-gray-300 p-3 rounded"
          style={receiptStyle}
        >
          <div className="text-center" style={{ fontSize: 28, fontWeight: 900 }}>** PREPARO **</div>
          <div className="text-center" style={{ fontSize: 18, marginTop: 4 }}>#00042</div>
          <div className="text-center" style={{ fontSize: 13 }}>14:35</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontWeight: 'bold' }}>CLIENTE: Maria Silva</div>
          <div className="text-center" style={{ fontSize: 14 }}>MESA: 03</div>
          <div className="text-center" style={{ fontSize: 14 }}>COMANDA: #7</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontWeight: 'bold' }}>— ITENS —</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 'bold' }}>2x X-Tudo → COMBO</div>
            <div style={{ marginLeft: 10, fontSize: 18, color: '#333' }}>→ Carne: Ponto médio</div>
            <div style={{ marginLeft: 10, fontSize: 18, color: '#333' }}>→ Bebida: Coca 350ml</div>
            <div style={{ marginLeft: 10, fontSize: 18, color: '#333' }}>{">>> "}Sem cebola</div>
          </div>
          <div style={{ fontSize: 14, fontWeight: 'bold', color: '#333', margin: '6px 0' }}>------------------</div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 'bold' }}>1x Batata P</div>
          </div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontSize: 11, color: '#333' }}>24/01/2026 14:35:00</div>
        </div>
      </div>
    </div>
  );
}

// Preview do Cupom de Produção (estilo cupomDelivery.js — pedido/entrega)
function PreviewCupomProducao() {
  const receiptStyle = {
    width: 280,
    minHeight: 520,
    fontFamily: '"Courier New", monospace',
    fontSize: 13,
    fontWeight: 'bold',
    background: '#fff',
    color: '#000',
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Visualização de como o cupom de produção (pedido/entrega) será impresso.</p>
      <div className="flex justify-center p-6 bg-slate-100 dark:bg-slate-900/50 rounded-xl">
        <div
          className="shadow-lg border border-gray-300 p-3 rounded"
          style={receiptStyle}
        >
          <div style={{ width: 97, height: 97, background: '#e0e0e0', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#333' }}>LOGO</div>
          <div className="text-center" style={{ fontWeight: 'bold', fontSize: 16 }}>Núcleo</div>
          <div className="text-center" style={{ fontSize: 12, color: '#333', marginBottom: 4 }}>O Centro da sua Gestão</div>
          <div className="text-center" style={{ fontSize: 12, marginBottom: 4 }}>Rua Exemplo, 100 — Centro</div>
          <div className="text-center" style={{ fontSize: 12 }}>TEL: (34) 9.9672-7535</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontSize: 19, fontWeight: 'bold' }}>#00042</div>
          <div className="text-center" style={{ fontSize: 13 }}>24/01/2026, 14:35</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontWeight: 'bold' }}>— ITENS DO PEDIDO —</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#333', marginBottom: 4 }}>
            <span>QTD PREÇO ITEM</span><span>TOTAL</span>
          </div>
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>2 x 12,50 X-Tudo ///COMBO</span><span>R$ 25,00</span></div>
            <div style={{ fontSize: 12, color: '#333', marginLeft: 8 }}>• Carne: Ponto médio — R$ 0,00</div>
            <div style={{ fontSize: 12, color: '#333', marginLeft: 8 }}>• Bebida: Coca 350ml — R$ 5,00</div>
          </div>
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>1 x 15,00 Batata P</span><span>R$ 15,00</span></div>
          </div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>Taxa de Entrega:<span>R$ 5,00</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 15, marginTop: 4 }}>TOTAL:<span>R$ 50,00</span></div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontWeight: 'bold' }}>— PAGAR NA ENTREGA —</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
            <span>TOTAL</span><span style={{ flex: 1, borderBottom: '1px dotted #000', margin: '0 6px' }}></span><span>R$ 50,00</span>
          </div>
          <div style={{ marginTop: 4, fontWeight: 'bold' }}>PAGAMENTO: PIX</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontWeight: 'bold' }}>— INFORMAÇÕES DE ENTREGA —</div>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>CLIENTE: Maria Silva</div>
          <div style={{ fontWeight: 'bold', fontSize: 15 }}>TEL: (34) 9.9672-7535</div>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>END: Rua Exemplo, 100</div>
          <div style={{ fontWeight: 'bold', fontSize: 15 }}>BAIRRO: Centro</div>
          <div style={{ borderBottom: '1px dashed #000', margin: '6px 0' }}></div>
          <div className="text-center" style={{ fontSize: 11, color: '#333' }}>NÃO É DOCUMENTO FISCAL</div>
        </div>
      </div>
    </div>
  );
}

// ==================== ABA INTEGRAÇÃO ====================
function IntegracaoTab({ toast }) {
  const [selecionada, setSelecionada] = useState("ifood");

  // Catálogo de integrações. `emBreve` só marca o card e bloqueia a seleção —
  // não existe painel pra elas ainda.
  const INTEGRACOES = [
    { id: "ifood",       nome: "iFood",        logo: "/logo-ifood.png",       desc: "Receba os pedidos do iFood direto no sistema" },
    { id: "mercadopago", nome: "Mercado Pago", logo: "/logo-mercadopago.png", desc: "PIX online e cartão no cardápio digital" },
    { id: "entregaai",   nome: "UP Entrega",   logo: "/logo-upentrega-v3.png", desc: "Despache entregas para a frota parceira" },
    { id: "metapixel",   nome: "Meta Pixel",   logo: "/logo-meta.svg",        desc: "Meça o funil do seu cardápio no Facebook/Instagram Ads" },
    { id: "nfe",         nome: "NFe Fácil",    logo: "/logo-nfe.svg",         desc: "Emita nota fiscal do consumidor (NFC-e) direto da venda" },
    // Balança (26/10/2026): sem logo de marca — o ícone de balança no lugar
    { id: "balanca",     nome: "Balança",      Icon: Scale,                   desc: "Peso direto da balança no Balcão, Comandas, Mesas e Delivery" },
    { id: "asaas",       nome: "Asaas",        logo: "/logo-asaas.png",       desc: "Cobranças, boletos e gestão financeira", emBreve: true },
    { id: "99food",      nome: "99Food",       logo: "/logo-99food.png",      desc: "Pedidos do 99Food no seu painel", emBreve: true },
    { id: "aiqfome",     nome: "aiqfome",      logo: "/logo-aiqfome.png",     desc: "Pedidos do aiqfome no seu painel", emBreve: true },
    { id: "keeta",       nome: "Keeta",        logo: "/logo-keeta.png",       desc: "Pedidos da Keeta no seu painel", emBreve: true },
    { id: "deliverymuch", nome: "Delivery Much", logo: "/logo-deliverymuch.png", desc: "Pedidos do Delivery Much no seu painel", emBreve: true },
  ];

  const atual = INTEGRACOES.find((i) => i.id === selecionada);

  return (
    // Lista à esquerda, detalhe à direita. Em telas estreitas vira uma coluna
    // só: a lista em cima e o painel embaixo, porque 2 colunas num celular
    // deixariam ambas ilegíveis.
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* Lista */}
      <div className="w-full lg:w-72 lg:shrink-0 space-y-2">
        {INTEGRACOES.map((it) => {
          const ativa = selecionada === it.id;
          return (
            <button
              key={it.id}
              onClick={() => !it.emBreve && setSelecionada(it.id)}
              disabled={it.emBreve}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                ativa
                  ? "border-purple-500 bg-purple-500/5 shadow-sm"
                  : it.emBreve
                    ? "border-border bg-muted/30 cursor-not-allowed opacity-70"
                    : "border-border hover:border-purple-300 hover:bg-muted/40"
              }`}
              title={it.emBreve ? `${it.nome} — em breve` : it.nome}
            >
              {it.Icon ? (
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center shrink-0">
                  <it.Icon className="w-5 h-5" />
                </div>
              ) : (
              <img
                src={it.logo}
                alt={it.nome}
                className={`w-10 h-10 rounded-lg object-contain shrink-0 ${it.emBreve ? "grayscale" : ""}`}
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{it.nome}</span>
                  {it.emBreve && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0">
                      Em breve
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{it.desc}</p>
              </div>
              {!it.emBreve && <ChevronRight className={`w-4 h-4 shrink-0 ${ativa ? "text-purple-500" : "text-muted-foreground/40"}`} />}
            </button>
          );
        })}
      </div>

      {/* Painel da integração selecionada */}
      <div className="flex-1 min-w-0 w-full">
        {atual && (
          <div className="flex items-center gap-3 pb-4 mb-4 border-b">
            {atual.Icon ? <atual.Icon className="w-8 h-8 text-orange-500" /> : (
            <img src={atual.logo} alt="" className="w-8 h-8 rounded-md object-contain"
                 onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
            )}
            <div>
              <h2 className="font-bold text-lg leading-tight">{atual.nome}</h2>
              <p className="text-xs text-muted-foreground">{atual.desc}</p>
            </div>
          </div>
        )}
        {selecionada === "ifood" && <IFoodConfig embedded />}
        {selecionada === "mercadopago" && <MercadoPagoSubTab toast={toast} />}
        {selecionada === "entregaai" && <EntregaAiSubTab toast={toast} />}
        {selecionada === "metapixel" && <MetaPixelSubTab toast={toast} />}
        {selecionada === "balanca" && <IntegracaoBalanca />}
        {selecionada === "nfe" && (
          <FiscalLock>
            <ConfiguracaoFiscal />
          </FiscalLock>
        )}
      </div>
    </div>
  );
}

// Sub-aba Meta Pixel — ID do pixel por loja. Salvo em /company/settings (mesma
// origem que o cardápio público lê), então o pixel chega no funil sem esforço.
function MetaPixelSubTab({ toast }) {
  const [pixelId, setPixelId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/company/settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        setPixelId((data && data.meta_pixel_id) || "");
      } catch (_) {
        /* silencioso */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    const id = pixelId.trim();
    // Verificação de FORMATO: o Pixel ID tem 15-16 dígitos (só números). Vazio é
    // permitido (desativa o rastreamento). Não dá pra checar se o pixel existe de
    // verdade daqui — ele vive na conta Meta da loja, sem acesso do Núcleo.
    if (id && !/^\d{15,16}$/.test(id)) {
      toast({
        title: "ID do Pixel inválido",
        description: "O ID tem 15 ou 16 dígitos (só números). Confira se copiou só o número — não o código inteiro.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/company/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ meta_pixel_id: id }),
      });
      if (res.ok) {
        toast({
          title: id ? "Pixel conectado ✅" : "Rastreamento desativado",
          description: id
            ? "Salvo! Pra confirmar que está medindo: abra seu cardápio e veja no Gerenciador de Eventos → Eventos de teste (pode levar alguns minutos)."
            : "O Meta Pixel foi removido do seu cardápio.",
        });
      } else {
        toast({ title: "Erro", description: "Não foi possível salvar.", variant: "destructive" });
      }
    } catch (_) {
      toast({ title: "Erro", description: "Falha ao salvar.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5 max-w-xl">
        <CarregandoFormulario campos={1} />
        <CarregandoBloco altura="h-80" />
      </div>
    );
  }

  const passos = [
    <>Acesse o <b>Gerenciador de Eventos</b> da Meta (login com o Facebook da sua empresa) e clique em <b>Conectar dados</b>.</>,
    <>Em <b>“Conecte uma nova fonte de dados”</b>: escolha <b>Web</b> e avance.</>,
    <>Em <b>“Criar um conjunto de dados”</b>: dê um <b>Nome</b> (ex.: <i>“Cardápio — Sua Loja”</i>). A <b>categoria é opcional — pode ignorar</b> (deixe em branco; são categorias sensíveis que não valem pra um cardápio). Pode manter marcado o <b>“Adicione a API de Conversões”</b>. Clique em <b>Criar</b>.</>,
    <>Em <b>“Conecte seus dados da web”</b>: escolha <b>“Configurar o Pixel da Meta”</b> (já vem marcado) e clique em <b>Avançar</b>.</>,
    <>Em <b>“Configurar o Pixel da Meta”</b>: escolha <b>“Adicionar o código do Pixel da Meta ao site por conta própria”</b> (Recomendado) e clique em <b>Avançar</b>.</>,
    <>Na tela <b>“Copiar código de base”</b>: <b>você NÃO precisa desse código</b> — a Núcleo já injeta o pixel no seu cardápio. Pode <b>fechar o assistente (X)</b>.</>,
    <><b>Pegue o ID:</b> no Gerenciador de Eventos, em <b>Visão geral</b> (ou <b>Conjuntos de dados</b>), clique no seu conjunto — logo abaixo do nome aparece <b>“Identificação: número”</b>. Esse número (16 dígitos) é o seu <b>Pixel ID</b>.</>,
    <>Copie esse número, cole no campo <b>aqui em cima</b> e clique em <b>Salvar</b>. Pronto! ✅</>,
  ];

  return (
    <div className="space-y-5 max-w-xl">
      <p className="text-sm text-muted-foreground">
        Conecte o <b>Meta Pixel</b> (Facebook/Instagram) da sua loja pra medir o funil do
        seu cardápio — visitas, produtos vistos, itens no carrinho e pedidos concluídos —
        e otimizar seus anúncios.
      </p>

      {/* Campo do ID */}
      <div>
        <label className="text-sm font-medium">ID do Meta Pixel</label>
        <Input
          value={pixelId}
          onChange={(e) => setPixelId(e.target.value.replace(/\D/g, ""))}
          placeholder="Ex.: 1055682880591960"
          inputMode="numeric"
          className="mt-1"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Só números. Deixe em branco para desativar o rastreamento.
        </p>
        <Button onClick={handleSave} disabled={saving} className="mt-3">
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </div>

      {/* Tutorial: como conseguir o ID */}
      <div className="rounded-xl border bg-muted/30 p-4">
        <h3 className="font-semibold text-sm mb-3">Como conseguir o ID do Pixel</h3>
        <ol className="space-y-3">
          {passos.map((txt, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="flex-none w-6 h-6 rounded-full bg-purple-500/15 text-purple-600 dark:text-purple-400 font-semibold text-xs grid place-items-center">
                {i + 1}
              </span>
              <span className="text-muted-foreground pt-0.5">{txt}</span>
            </li>
          ))}
        </ol>
        <a
          href="https://business.facebook.com/events_manager2/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-purple-600 dark:text-purple-400 hover:underline"
        >
          Abrir o Gerenciador de Eventos ↗
        </a>
        <p className="text-xs text-muted-foreground mt-3">
          Depois de salvar, acompanhe as visitas e pedidos no próprio Gerenciador de Eventos
          (abas <b>Visão geral</b> e <b>Eventos de teste</b>). Os dados podem levar até 30 min pra aparecer.
        </p>
      </div>
    </div>
  );
}

// Sub-aba UP Entrega
function EntregaAiSubTab({ toast }) {
  const [config, setConfig] = useState({
    api_key: "",
    api_key_masked: "",
    api_key_configured: false,
    enabled: false,
    auto_dispatch: false,
    webhook_secret: "",
    webhook_secret_masked: "",
    webhook_secret_configured: false,
    store_name: "",
    store_phone: "",
    store_address: "",
    store_lat: "",
    store_lng: "",
    store_city: "",
    store_neighborhood: "",
  });
  const [loading, setLoading] = useState(false);
  // Esqueleto até a 1ª leitura: sem ele o cartão nascia "Desativado" e com as
  // chaves desligadas antes de saber o que a loja gravou (25/10/2026).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeSection, setActiveSection] = useState("config");
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  // Motivo de não ter saldo (ex.: integração sem chave) — antes a tela ficava muda (25/10/2026)
  const [walletErro, setWalletErro] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [estimateData, setEstimateData] = useState({ latitude: "", longitude: "" });
  const [estimateResult, setEstimateResult] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/entregaai/config`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConfig(prev => ({ ...prev, ...data, api_key: "", webhook_secret: "" }));
      }
    } catch (error) {
      console.error("Erro ao carregar config UP Entrega:", error);
    } finally {
      setPrimeiraCarga(false);
    }
  };

  const saveConfig = async () => {
    try {
      setLoading(true);
      const payload = {
        enabled: config.enabled,
        auto_dispatch: config.auto_dispatch,
        // Faltava aqui: sem mandar, a chave voltava ligada a cada recarga.
        cash_confirmation: config.cash_confirmation !== false,
      };
      if (config.api_key) payload.api_key = config.api_key;
      if (config.webhook_secret) payload.webhook_secret = config.webhook_secret;

      const res = await fetch(`${API_URL}/api/entregaai/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast({ title: "Sucesso", description: "Configurações do UP Entrega salvas!" });
        await fetchConfig();
      } else {
        toast({ title: "Erro", description: "Erro ao salvar configurações", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erro", description: "Erro ao salvar configurações", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const payload = config.api_key ? { api_key: config.api_key } : {};
      const res = await fetch(`${API_URL}/api/entregaai/test-connection`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setTestResult(data);
      // Recarregar config para pegar dados do estabelecimento sincronizados
      if (data.success) {
        await fetchConfig();
      }
    } catch (error) {
      setTestResult({ success: false, message: "Erro ao testar conexão" });
    } finally {
      setTesting(false);
    }
  };

  const fetchWallet = async () => {
    try {
      setWalletLoading(true);
      const res = await fetch(`${API_URL}/api/entregaai/wallet/balance`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setWallet(data.data || data);
        setWalletErro("");
      } else {
        const corpo = await res.json().catch(() => ({}));
        setWallet(null);
        setWalletErro(typeof corpo?.detail === "string" ? corpo.detail : "Não foi possível consultar o saldo agora.");
      }
    } catch (error) {
      console.error("Erro ao carregar carteira:", error);
    } finally {
      setWalletLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTxLoading(true);
      const res = await fetch(`${API_URL}/api/entregaai/wallet/transactions?limit=20`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const txList = data.data?.transactions || data.transactions || (Array.isArray(data.data) ? data.data : []);
        setTransactions(Array.isArray(txList) ? txList : []);
      }
    } catch (error) {
      console.error("Erro ao carregar transações:", error);
    } finally {
      setTxLoading(false);
    }
  };

  const estimatePrice = async () => {
    try {
      setEstimating(true);
      setEstimateResult(null);
      const res = await fetch(`${API_URL}/api/entregaai/pricing/estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ destination: estimateData }),
      });
      const data = await res.json();
      if (res.ok) {
        setEstimateResult(data.data || data);
      } else {
        setEstimateResult({ error: data.detail || "Erro na estimativa" });
      }
    } catch (error) {
      setEstimateResult({ error: "Erro ao estimar preço" });
    } finally {
      setEstimating(false);
    }
  };

  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const sections = [
    { id: "config", label: "Configurações", icon: Settings },
    { id: "wallet", label: "Carteira", icon: Wallet },
    { id: "estimate", label: "Estimativa", icon: Navigation },
  ];

  if (primeiraCarga) {
    return (
      <div className="space-y-6">
        <CarregandoBloco altura="h-36" />
        <CarregandoFormulario campos={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-xl border shadow-sm">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <img src="/logo-upentrega-v3.png" alt="UP Entrega" className="w-12 h-12 rounded-xl object-contain" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold">UP Entrega</h2>
              <p className="text-sm text-muted-foreground">Entregas sob demanda com motoristas próprios</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              config.enabled
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
            }`}>
              {config.enabled ? "Ativo" : "Desativado"}
            </div>
          </div>
        </div>

        {/* Sub-navegação */}
        <div className="flex gap-1 px-6 border-b overflow-x-auto justify-start sm:justify-center">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => {
                  setActiveSection(s.id);
                  if (s.id === "wallet") { fetchWallet(); fetchTransactions(); }
                }}
                className={`px-4 py-2.5 font-medium text-sm border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  activeSection === s.id
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Seção Configurações */}
      {activeSection === "config" && (
        <div className="bg-card rounded-xl border shadow-sm">
          <div className="p-6 space-y-6">
            {/* Status toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-3">
                {config.enabled ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-gray-400" />
                )}
                <div>
                  <p className="font-medium">Integração UP Entrega</p>
                  <p className="text-sm text-muted-foreground">
                    {config.enabled ? "Ativa - Entregas serão despachadas pelo UP Entrega" : "Desativada"}
                  </p>
                </div>
              </div>
              <Toggle
                checked={config.enabled}
                onChange={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
              />
            </div>

            {/* Confirmação de pagamento em dinheiro */}
            {/* gap + shrink-0: o texto longo esmagava o ícone e a chave (a bolinha saía do trilho) */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-3">
                <Banknote className="w-6 h-6 text-amber-500 shrink-0" />
                <div>
                  <p className="font-medium">Confirmação de pagamento em dinheiro</p>
                  <p className="text-sm text-muted-foreground">
                    Ligada: o entregador só finaliza a entrega depois que você confirma que recebeu o dinheiro (botão "Confirmar recebimento" na tela Delivery ou no app da UP). Desligada: ele finaliza sem esperar a sua confirmação. Não muda "já pago" nem o retorno ao estabelecimento — isso fica em Financeiro → Formas de Pagamento.
                  </p>
                </div>
              </div>
              <Toggle
                checked={config.cash_confirmation !== false}
                onChange={() => setConfig(prev => ({ ...prev, cash_confirmation: !(prev.cash_confirmation !== false) }))}
              />
            </div>

            {/* Credenciais */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Credenciais
              </h3>

              <div className="grid gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">API Key</label>
                    {config.api_key_configured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle className="w-3 h-3" /> Salva
                      </span>
                    )}
                  </div>
                  <InputSecret
                    value={config.api_key}
                    onChange={(e) => setConfig(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder={config.api_key_configured ? `Configurada: ${config.api_key_masked}` : "Sua API Key do UP Entrega"}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {config.api_key_configured
                      ? `Salva permanentemente (${config.api_key_masked}). Deixe em branco para manter a atual.`
                      : "Obtenha sua API Key no painel do UP Entrega"}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Webhook Secret (opcional)</label>
                    {config.webhook_secret_configured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle className="w-3 h-3" /> Salvo
                      </span>
                    )}
                  </div>
                  <InputSecret
                    value={config.webhook_secret}
                    onChange={(e) => setConfig(prev => ({ ...prev, webhook_secret: e.target.value }))}
                    placeholder={config.webhook_secret_configured ? `Configurado: ${config.webhook_secret_masked}` : "Secret para verificar assinatura dos webhooks"}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {config.webhook_secret_configured
                      ? `Salvo permanentemente (${config.webhook_secret_masked}). Deixe em branco para manter o atual.`
                      : "Usado para verificar a autenticidade dos webhooks recebidos"}
                  </p>
                </div>
              </div>

              {/* Botão testar conexão */}
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={testConnection}
                  disabled={testing}
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testando...
                    </>
                  ) : (
                    <>
                      <Wifi className="w-4 h-4 mr-2" />
                      Testar Conexão
                    </>
                  )}
                </Button>
                {testResult && (
                  <span className={`text-sm flex items-center gap-1 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                    {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {testResult.message}
                  </span>
                )}
              </div>
            </div>

            {/* Dados do Estabelecimento (vem da API) */}
            {config.store_name && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Estabelecimento Vinculado
                </h3>
                <p className="text-sm text-muted-foreground -mt-2">
                  Dados sincronizados automaticamente do UP Entrega
                </p>

                <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <Store className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-base">{config.store_name}</p>
                      {config.store_phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" /> {config.store_phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {config.store_address && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span>
                        {config.store_address}
                        {config.store_neighborhood && `, ${config.store_neighborhood}`}
                        {config.store_city && ` - ${config.store_city}`}
                      </span>
                    </div>
                  )}

                  {config.store_lat && config.store_lng && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                      <Navigation className="w-3.5 h-3.5" />
                      {config.store_lat}, {config.store_lng}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Opções */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Opções
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <span className="text-sm font-medium">Despacho automático</span>
                    <p className="text-xs text-muted-foreground">Ao confirmar pedido de entrega, solicitar motorista automaticamente</p>
                  </div>
                  <Toggle
                    checked={config.auto_dispatch}
                    onChange={() => setConfig(prev => ({ ...prev, auto_dispatch: !prev.auto_dispatch }))}
                  />
                </div>
              </div>
            </div>

            {/* Webhook URL Info */}
            <div className="space-y-3 pt-4 border-t">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Link className="w-5 h-5" />
                Webhook
              </h3>
              <p className="text-sm text-muted-foreground">
                Configure esta URL no painel do UP Entrega para receber atualizações de status em tempo real:
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono break-all">
                  {window.location.origin}/api/entregaai/webhook
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/api/entregaai/webhook`);
                    toast({ title: "Copiado!", description: "URL do webhook copiada" });
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Botão Salvar */}
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={saveConfig} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Configurações
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Seção Carteira */}
      {activeSection === "wallet" && (
        <div className="space-y-6">
          {/* Saldo */}
          <div className="bg-card rounded-xl border shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Saldo da Carteira
              </h3>
              <Button variant="outline" size="sm" onClick={fetchWallet} disabled={walletLoading}>
                {walletLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            {wallet ? (
              <div className="text-3xl font-bold text-emerald-600">
                R$ {(wallet.balance ?? wallet.amount ?? 0).toFixed(2).replace(".", ",")}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                {walletLoading ? "Carregando..." : walletErro || "Clique em atualizar para consultar o saldo"}
              </p>
            )}
          </div>

          {/* Transações */}
          <div className="bg-card rounded-xl border shadow-sm">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Últimas Transações
              </h3>
              <Button variant="outline" size="sm" onClick={fetchTransactions} disabled={txLoading}>
                {txLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            <div className="divide-y">
              {transactions.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  {txLoading ? "Carregando transações..." : "Nenhuma transação encontrada"}
                </div>
              ) : (
                transactions.map((tx, i) => (
                  <div key={tx.id || i} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        (tx.type === "credit" || tx.amount > 0)
                          ? "bg-green-100 text-green-600 dark:bg-green-900/30"
                          : "bg-red-100 text-red-600 dark:bg-red-900/30"
                      }`}>
                        {(tx.type === "credit" || tx.amount > 0) ? <Plus className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{tx.description || tx.type || "Transação"}</p>
                        <p className="text-xs text-muted-foreground">
                          {tx.created_at || tx.createdAt || tx.date || ""}
                        </p>
                      </div>
                    </div>
                    <span className={`font-semibold ${
                      (tx.type === "credit" || tx.amount > 0) ? "text-green-600" : "text-red-600"
                    }`}>
                      {(tx.type === "credit" || tx.amount > 0) ? "+" : "-"} R$ {Math.abs(tx.amount || tx.value || 0).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Seção Estimativa */}
      {activeSection === "estimate" && (
        <div className="bg-card rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
            <Navigation className="w-5 h-5" />
            Estimativa de Preço
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Calcule o preço de uma entrega sem criar o pedido. Informe as coordenadas do destino.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <div>
              <label className="text-sm font-medium">Latitude do destino</label>
              <Input
                value={estimateData.latitude}
                onChange={(e) => setEstimateData(prev => ({ ...prev, latitude: e.target.value }))}
                placeholder="-19.9200"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Longitude do destino</label>
              <Input
                value={estimateData.longitude}
                onChange={(e) => setEstimateData(prev => ({ ...prev, longitude: e.target.value }))}
                placeholder="-43.9400"
                className="mt-1 font-mono"
              />
            </div>
          </div>

          <Button onClick={estimatePrice} disabled={estimating || !estimateData.latitude || !estimateData.longitude}>
            {estimating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <DollarSign className="w-4 h-4 mr-2" />
                Calcular Estimativa
              </>
            )}
          </Button>

          {estimateResult && (
            <div className={`mt-4 p-4 rounded-lg border ${estimateResult.error ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200'}`}>
              {estimateResult.error ? (
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">{estimateResult.error}</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold text-lg">
                      R$ {(estimateResult.price ?? estimateResult.estimatedPrice ?? estimateResult.fee ?? 0).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                  {(estimateResult.distance || estimateResult.estimatedDistance) && (
                    <p className="text-sm text-muted-foreground">
                      Distância: {(estimateResult.distance || estimateResult.estimatedDistance || 0).toFixed(1)} km
                    </p>
                  )}
                  {(estimateResult.duration || estimateResult.estimatedDuration) && (
                    <p className="text-sm text-muted-foreground">
                      Tempo estimado: {estimateResult.duration || estimateResult.estimatedDuration} min
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Sub-aba Mercado Pago (PIX)
function MercadoPagoSubTab({ toast }) {
  const [pixSettings, setPixSettings] = useState({
    mp_access_token: "",
    mp_public_key: "",
    mp_webhook_secret: "",
    mp_pix_expiration_minutes: 10,
    whatsapp_send_pix_automatically: true,
    cpf_required_for_pix: true,
    block_kitchen_until_paid: true,
    pix_enabled: false,
    mp_access_token_masked: "",
    mp_access_token_configured: false,
    mp_webhook_secret_masked: "",
    mp_webhook_secret_configured: false,
    mp_webhook_url: "",
  });
  const [webhookUrlCopied, setWebhookUrlCopied] = useState(false);
  // Esqueleto até a 1ª leitura: o PIX nascia "desligado" antes de carregar (25/10/2026).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixTesting, setPixTesting] = useState(false);
  const [pixTestResult, setPixTestResult] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchPixSettings();
  }, []);

  const fetchPixSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/pix`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPixSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Erro ao carregar configurações PIX:", error);
    } finally {
      setPrimeiraCarga(false);
    }
  };

  const savePixSettings = async () => {
    try {
      setPixLoading(true);
      const res = await fetch(`${API_URL}/api/settings/pix`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          mp_access_token: pixSettings.mp_access_token || undefined,
          mp_public_key: pixSettings.mp_public_key,
          mp_webhook_secret: pixSettings.mp_webhook_secret || undefined,
          mp_pix_expiration_minutes: pixSettings.mp_pix_expiration_minutes,
          whatsapp_send_pix_automatically: pixSettings.whatsapp_send_pix_automatically,
          cpf_required_for_pix: pixSettings.cpf_required_for_pix,
          block_kitchen_until_paid: pixSettings.block_kitchen_until_paid,
          pix_enabled: pixSettings.pix_enabled,
        }),
      });
      if (res.ok) {
        toast({ title: "Sucesso", description: "Configurações do PIX salvas!" });
        setPixSettings(prev => ({ ...prev, mp_access_token: "", mp_webhook_secret: "" }));
        await fetchPixSettings();
      } else {
        // 403: só dono e administrador gravam a credencial do PIX (achado 127)
        const erro = await res.json().catch(() => ({}));
        toast({ title: "Erro", description: erro.detail || "Erro ao salvar configurações do PIX", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erro", description: "Erro ao salvar configurações do PIX", variant: "destructive" });
    } finally {
      setPixLoading(false);
    }
  };

  const testPixConnection = async () => {
    try {
      setPixTesting(true);
      setPixTestResult(null);
      const payload = pixSettings.mp_access_token ? { mp_access_token: pixSettings.mp_access_token } : {};
      const res = await fetch(`${API_URL}/api/settings/pix/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setPixTestResult(res.ok ? data : { success: false, message: data.detail || "Erro ao testar conexão" });
    } catch (error) {
      setPixTestResult({ success: false, message: "Erro ao testar conexão" });
    } finally {
      setPixTesting(false);
    }
  };

  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (primeiraCarga) {
    return (
      <div className="space-y-6">
        <CarregandoBloco altura="h-28" />
        <CarregandoFormulario campos={5} />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border shadow-sm">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white text-2xl">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">PIX Online - Mercado Pago</h2>
            <p className="text-sm text-muted-foreground">Configure o pagamento automático via PIX</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Status */}
        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-3">
            {pixSettings.pix_enabled ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <XCircle className="w-6 h-6 text-gray-400" />
            )}
            <div>
              <p className="font-medium">PIX Online</p>
              <p className="text-sm text-muted-foreground">
                {pixSettings.pix_enabled ? "Ativo - Clientes podem pagar com PIX" : "Desativado"}
              </p>
            </div>
          </div>
          <Toggle
            checked={pixSettings.pix_enabled}
            onChange={() => setPixSettings(prev => ({ ...prev, pix_enabled: !prev.pix_enabled }))}
          />
        </div>

        {/* Credenciais */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Credenciais Mercado Pago
          </h3>

          <div className="grid gap-4">
            <div>
              <label className="text-sm font-medium">Access Token (Backend)</label>
              <div className="flex gap-2 mt-1">
                <InputSecret
                  value={pixSettings.mp_access_token}
                  onChange={(e) => setPixSettings(prev => ({ ...prev, mp_access_token: e.target.value }))}
                  placeholder={pixSettings.mp_access_token_configured ? `Configurado: ${pixSettings.mp_access_token_masked}` : "APP_USR-..."}
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {pixSettings.mp_access_token_configured
                  ? `Token configurado (${pixSettings.mp_access_token_masked})`
                  : "Obtenha em: Mercado Pago → Seu negócio → Configurações → Credenciais"}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Public Key (Frontend) - Opcional</label>
              <Input
                value={pixSettings.mp_public_key || ""}
                onChange={(e) => setPixSettings(prev => ({ ...prev, mp_public_key: e.target.value }))}
                placeholder="APP_USR-..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Necessário apenas para pagamento com cartão (futuro)
              </p>
            </div>
          </div>

          {/* Webhook */}
          <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Wifi className="w-4 h-4 text-blue-500 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-sm">Webhook (confirmação instantânea)</h4>
                <p className="text-xs text-muted-foreground">
                  Sem webhook, o sistema usa polling (confirma em 5-30s). Com webhook configurado,
                  o Mercado Pago nos avisa na hora que o cliente paga (&lt;1s).
                </p>
              </div>
              {pixSettings.mp_webhook_secret_configured && (
                <span className="text-[10px] font-bold bg-green-500/15 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> ATIVO
                </span>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">URL para cadastrar no Mercado Pago</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={pixSettings.mp_webhook_url || ""}
                  readOnly
                  className="font-mono text-xs bg-muted/50"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!pixSettings.mp_webhook_url) return;
                    navigator.clipboard.writeText(pixSettings.mp_webhook_url);
                    setWebhookUrlCopied(true);
                    toast({ title: "Copiado!", description: "URL do webhook copiada" });
                    setTimeout(() => setWebhookUrlCopied(false), 2000);
                  }}
                >
                  {webhookUrlCopied ? "Copiado!" : "Copiar"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Cole esta URL em: Mercado Pago &rarr; Suas integrações &rarr; Webhooks &rarr;
                Configurar notificações &rarr; URL de produção. Selecione o evento{" "}
                <strong>Pagamentos</strong>.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Chave secreta (Webhook Secret)</label>
              <InputSecret
                value={pixSettings.mp_webhook_secret || ""}
                onChange={(e) => setPixSettings(prev => ({ ...prev, mp_webhook_secret: e.target.value }))}
                placeholder={
                  pixSettings.mp_webhook_secret_configured
                    ? `Configurada: ${pixSettings.mp_webhook_secret_masked}`
                    : "Cole aqui a chave secreta do webhook"
                }
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {pixSettings.mp_webhook_secret_configured
                  ? `Salva. Deixe em branco para manter a atual.`
                  : "No painel do Mercado Pago, após cadastrar a URL, copie a \"Chave secreta\" gerada e cole aqui. Serve pra garantir que só o MP pode nos notificar."}
              </p>
            </div>
          </div>

          {/* Botão testar conexão */}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={testPixConnection}
              disabled={pixTesting}
            >
              {pixTesting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4 mr-2" />
                  Testar Conexão
                </>
              )}
            </Button>
            {pixTestResult && (
              <span className={`text-sm flex items-center gap-1 ${pixTestResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {pixTestResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {pixTestResult.message}
              </span>
            )}
          </div>
        </div>

        {/* Configurações */}
        <div className="space-y-4 pt-4 border-t">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurações
          </h3>

          <div>
            <label className="text-sm font-medium">Expiração do PIX (minutos)</label>
            <Input
              type="number"
              min="1"
              max="1440"
              value={pixSettings.mp_pix_expiration_minutes}
              onChange={(e) => setPixSettings(prev => ({ ...prev, mp_pix_expiration_minutes: parseInt(e.target.value) || 10 }))}
              className="mt-1 w-32"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tempo que o cliente tem para pagar (padrão: 10 min)
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <span className="text-sm font-medium">Enviar PIX via WhatsApp</span>
                <p className="text-xs text-muted-foreground">Envia automaticamente a chave copia-e-cola para o cliente</p>
              </div>
              <Toggle
                checked={pixSettings.whatsapp_send_pix_automatically}
                onChange={() => setPixSettings(prev => ({ ...prev, whatsapp_send_pix_automatically: !prev.whatsapp_send_pix_automatically }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <span className="text-sm font-medium">Exigir CPF</span>
                <p className="text-xs text-muted-foreground">Obrigatório pelo Mercado Pago para PIX</p>
              </div>
              <Toggle
                checked={pixSettings.cpf_required_for_pix}
                onChange={() => setPixSettings(prev => ({ ...prev, cpf_required_for_pix: !prev.cpf_required_for_pix }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <span className="text-sm font-medium">Bloquear cozinha até pagar</span>
                <p className="text-xs text-muted-foreground">Só libera produção após pagamento confirmado</p>
              </div>
              <Toggle
                checked={pixSettings.block_kitchen_until_paid}
                onChange={() => setPixSettings(prev => ({ ...prev, block_kitchen_until_paid: !prev.block_kitchen_until_paid }))}
              />
            </div>
          </div>
        </div>

        {/* Botão Salvar */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={savePixSettings} disabled={pixLoading}>
            {pixLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configurações
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==================== UTILIDADES DE COR ====================
// Converte hex (#FF6400) para HSL string "22 100% 50%"
function hexToHsl(hex) {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Converte HSL string "22 100% 50%" para hex
function hslToHex(hslStr) {
  const parts = hslStr.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return "#ff6400";
  const h = parseFloat(parts[0]) / 360;
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  let r, g, b2;
  if (s === 0) { r = g = b2 = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b2)}`;
}

// Aplica a cor primária nas CSS vars
function applyPrimaryColor(hslStr) {
  const root = document.documentElement;
  root.style.setProperty("--primary", hslStr);
  root.style.setProperty("--ring", hslStr);
  root.style.setProperty("--chart-1", hslStr);
}

// Aplica o tema (claro/escuro)
function applyTheme(theme) {
  if (theme === "dark") {
    document.documentElement.classList.add("dark");
    localStorage.setItem("darkMode", "true");
  } else {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", "false");
  }
  window.dispatchEvent(new CustomEvent("themeChange", { detail: { dark: theme === "dark" } }));
}

// Cores predefinidas
const PRESET_COLORS = [
  { hex: "#FF6400", label: "Laranja" },
  { hex: "#EF4444", label: "Vermelho" },
  { hex: "#F97316", label: "Tangerina" },
  { hex: "#EAB308", label: "Amarelo" },
  { hex: "#22C55E", label: "Verde" },
  { hex: "#14B8A6", label: "Teal" },
  { hex: "#3B82F6", label: "Azul" },
  { hex: "#6366F1", label: "Indigo" },
  { hex: "#8B5CF6", label: "Roxo" },
  { hex: "#EC4899", label: "Rosa" },
  { hex: "#06B6D4", label: "Cyan" },
  { hex: "#78716C", label: "Neutro" },
];

// A cor é gravada em HSL arredondado, e a volta para hex perde precisão
// (Azul #3B82F6 → "217 91% 60%" → #3C83F6): comparar pelo HSL arredondado,
// senão a cor pronta escolhida nunca ficava marcada (achado 335).
const _hslArredondado = (hslStr) => (String(hslStr || "").match(/[\d.]+/g) || [])
  .slice(0, 3).map((n) => Math.round(parseFloat(n))).join(" ");
const mesmaCorHsl = (a, b) => _hslArredondado(a) === _hslArredondado(b);

// Hex para mostrar da cor gravada: o da cor pronta quando é uma delas.
function hexDaCor(hslStr) {
  const pronta = PRESET_COLORS.find((c) => mesmaCorHsl(hexToHsl(c.hex), hslStr));
  return pronta ? pronta.hex : hslToHex(hslStr);
}

// ==================== ABA APARÊNCIA ====================
function AparenciaTab() {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState(() => document.documentElement.classList.contains("dark") ? "dark" : "light");
  const [primaryHsl, setPrimaryHsl] = useState("22 100% 50%");
  const [colorHistory, setColorHistory] = useState([]);
  const [customHex, setCustomHex] = useState("#FF6400");
  // Esqueleto até a 1ª leitura: antes a "Cor atual" nascia laranja (#FF6400, o
  // padrão) e trocava para a cor da loja um instante depois (25/10/2026).
  const [carregado, setCarregado] = useState(false);

  // Carregar settings do backend
  useEffect(() => {
    const load = async () => {
      try {
        const tok = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        const data = await res.json();
        if (data.primary_color) {
          setPrimaryHsl(data.primary_color);
          setCustomHex(hexDaCor(data.primary_color));
          applyPrimaryColor(data.primary_color);
        }
        if (data.theme === "dark" || data.theme === "light") {
          setTheme(data.theme);
          guardarPadraoDaLoja(data.theme);
        }
        if (data.color_history) {
          try { setColorHistory(JSON.parse(data.color_history)); } catch (_) {}
        }
      } catch (_) {}
      setCarregado(true);
    };
    load();
  }, []);

  const handleSave = async (newTheme, newColor, newHistory) => {
    setSaving(true);
    try {
      const tok = localStorage.getItem("token");
      const body = {
        theme: newTheme || theme,
        primary_color: newColor || primaryHsl,
        color_history: JSON.stringify(newHistory || colorHistory),
      };
      await fetch(`${API_URL}/api/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: JSON.stringify(body),
      });
      toast({ title: "Aparência salva!", description: "As configurações foram aplicadas em todos os dispositivos." });
    } catch (_) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setSaving(false);
  };

  // Tema PADRÃO da loja: vale para quem não escolheu um tema só para si no
  // menu da foto. Quem escolheu continua vendo o dele (features/tema).
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    guardarPadraoDaLoja(newTheme);
    if (!escolhaDoUsuario()) applyTheme(newTheme);
    handleSave(newTheme, null, null);
  };

  const handleColorSelect = (hex) => {
    const hsl = hexToHsl(hex);
    // Adicionar cor atual ao histórico (máx 2)
    const currentHex = hexDaCor(primaryHsl);
    let newHistory = [...colorHistory];
    if (currentHex.toLowerCase() !== hex.toLowerCase()) {
      newHistory = [currentHex, ...newHistory.filter(c => c.toLowerCase() !== currentHex.toLowerCase() && c.toLowerCase() !== hex.toLowerCase())].slice(0, 2);
    }
    setPrimaryHsl(hsl);
    setCustomHex(hex);
    setColorHistory(newHistory);
    applyPrimaryColor(hsl);
    handleSave(null, hsl, newHistory);
  };

  const isDark = theme === "dark";

  if (!carregado) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <CarregandoBloco altura="h-[294px]" />
        <CarregandoBloco altura="h-[420px]" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* TEMA */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          {isDark ? <Moon className="w-5 h-5 text-blue-400" /> : <Sun className="w-5 h-5 text-yellow-500" />}
          <h2 className="text-lg font-bold">Tema Padrão</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          É o tema da loja: vale para todos os usuários, em todos os aparelhos.
          Quem trocar o tema pelo menu da foto (canto superior direito) fica com
          o tema que escolheu só para si.
        </p>
        {escolhaDoUsuario() && (
          <p className="text-xs text-muted-foreground -mt-2 mb-4">
            Você escolheu o tema {escolhaDoUsuario() === "dark" ? "escuro" : "claro"} só para você,
            então a sua tela não muda ao trocar o padrão aqui.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleThemeChange("light")}
            className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
              !isDark
                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-md"
                : "border-muted hover:border-muted-foreground/30"
            }`}
          >
            {!isDark && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-blue-500" /></div>}
            <div className="w-14 h-10 rounded-lg bg-white border shadow-sm flex items-center justify-center">
              <Sun className="w-5 h-5 text-yellow-500" />
            </div>
            <span className="font-semibold text-sm">Claro</span>
          </button>
          <button
            onClick={() => handleThemeChange("dark")}
            className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
              isDark
                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-md"
                : "border-muted hover:border-muted-foreground/30"
            }`}
          >
            {isDark && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-blue-500" /></div>}
            <div className="w-14 h-10 rounded-lg bg-zinc-800 border border-zinc-600 shadow-sm flex items-center justify-center">
              <Moon className="w-5 h-5 text-blue-400" />
            </div>
            <span className="font-semibold text-sm">Escuro</span>
          </button>
        </div>
      </div>

      {/* COR DOS BOTÕES */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Palette className="w-5 h-5" style={{ color: hslToHex(primaryHsl) }} />
          <h2 className="text-lg font-bold">Cor dos Botões</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Toda cor de destaque do sistema será substituída pela cor escolhida aqui.
        </p>

        {/* Preview */}
        <div className="flex items-center gap-3 mb-5 p-3 rounded-lg bg-muted/50">
          <div className="w-10 h-10 rounded-full shadow-inner" style={{ backgroundColor: hslToHex(primaryHsl) }}></div>
          <div className="flex-1">
            <p className="text-sm font-bold">Cor atual</p>
            <p className="text-xs text-muted-foreground font-mono">{hexDaCor(primaryHsl).toUpperCase()}</p>
          </div>
          <button
            className="h-9 px-4 rounded-lg font-semibold text-white text-sm"
            style={{ backgroundColor: `hsl(${primaryHsl})` }}
          >
            Exemplo
          </button>
        </div>

        {/* Preset Colors — 4 por linha no celular (6 cortava os nomes: "Lar...") */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
          {PRESET_COLORS.map((c) => {
            const isSelected = mesmaCorHsl(hexToHsl(c.hex), primaryHsl);
            return (
              <button
                key={c.hex}
                onClick={() => handleColorSelect(c.hex)}
                className={`group relative flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                  isSelected ? "bg-muted ring-2 ring-offset-2 ring-blue-500" : "hover:bg-muted/50"
                }`}
                title={c.label}
              >
                <div
                  className="w-8 h-8 rounded-full shadow-sm transition-transform group-hover:scale-110"
                  style={{ backgroundColor: c.hex }}
                >
                  {isSelected && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white drop-shadow" />
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground truncate w-full text-center">{c.label}</span>
              </button>
            );
          })}
        </div>

        {/* Custom Color */}
        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg border border-dashed">
          <label className="text-sm font-medium shrink-0">Cor personalizada:</label>
          <input
            type="color"
            value={customHex}
            onChange={(e) => {
              setCustomHex(e.target.value);
              handleColorSelect(e.target.value);
            }}
            className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0"
          />
          <Input
            value={customHex.toUpperCase()}
            onChange={(e) => {
              const v = e.target.value;
              setCustomHex(v);
              if (/^#[0-9a-fA-F]{6}$/.test(v)) handleColorSelect(v);
            }}
            className="w-28 font-mono text-sm h-9"
            maxLength={7}
            placeholder="#FF6400"
          />
        </div>

        {/* Histórico */}
        {colorHistory.length > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
            <History className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground shrink-0">Recentes:</span>
            <div className="flex gap-2">
              {colorHistory.map((hex, i) => (
                <button
                  key={i}
                  onClick={() => handleColorSelect(hex)}
                  // min-h no toque: 28px de altura é pequeno demais pro dedo.
                  // Cresce só no mobile pra não inflar a UI no desktop.
                  className="group flex items-center gap-1.5 px-2 py-1 min-h-[44px] sm:min-h-0 rounded-md hover:bg-muted transition-colors"
                  title={`Usar ${hex.toUpperCase()}`}
                >
                  <div className="w-5 h-5 rounded-full shadow-sm border" style={{ backgroundColor: hex }}></div>
                  <span className="text-xs font-mono text-muted-foreground group-hover:text-foreground">{hex.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {saving && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Salvando...
        </div>
      )}
    </div>
  );
}


// ==================== ABA EMPRESA ====================
function EmpresaTab({ toast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    company_name: "Núcleo",
    slogan: "O Centro da sua Gestão",
    logo_url: null,
    address: "",
    cep: "",
    logradouro: "",
    numero: "",
    bairro: "",
    cidade: "",
    uf: "",
    cnpj: "",
    fantasy_name: "",
    legal_name: "",
    founding_date: "",
    state_registration: "",
    city_registration: "",
    instagram: "",
    facebook: "",
    email: "",
    tiktok: "",
    kwai: "",
    phone: "",
    integration_source: ""
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/company/settings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      setFormData(data);
    } catch (error) {
      console.error("Erro ao buscar configurações:", error);
    } finally {
      setLoading(false);
    }
  };

  // O CEP e a autoridade sobre cidade e UF, entao sobrescreve. Rua e bairro
  // so vem quando o ViaCEP conhece (CEP de cidade inteira nao traz logradouro).
  const buscarCep = async (valor) => {
    const n = (valor || "").replace(/\D/g, "");
    if (n.length !== 8) return;
    setBuscandoCep(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${n}/json/`);
      const d = await r.json();
      if (d.erro) {
        toast({ title: "CEP não encontrado", variant: "destructive" });
        return;
      }
      setFormData((prev) => ({
        ...prev,
        cidade: d.localidade || prev.cidade,
        uf: d.uf || prev.uf,
        logradouro: d.logradouro || prev.logradouro,
        bairro: d.bairro || prev.bairro
      }));
    } catch {
      toast({ title: "Não foi possível consultar o CEP", variant: "destructive" });
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/company/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast({
          title: "Salvo!",
          description: "Configurações da empresa atualizadas"
        });
      } else {
        throw new Error("Erro ao salvar");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Erro",
        description: "O arquivo deve ser uma imagem",
        variant: "destructive"
      });
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 5MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const res = await fetch(`${API_URL}/api/company/logo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formDataUpload
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setFormData(prev => ({ ...prev, logo_url: data.logo_url }));
        toast({
          title: "Logo atualizada!",
          description: "A nova logo foi salva com sucesso"
        });
      } else {
        throw new Error(data.detail || "Erro ao fazer upload");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/company/logo`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        setFormData(prev => ({ ...prev, logo_url: null }));
        toast({
          title: "Logo removida",
          description: "A logo foi removida com sucesso"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível remover a logo",
        variant: "destructive"
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Carregamento com cards (25/10/2026): a ficha da empresa pisca até chegar —
  // nunca mostra "Núcleo" e campos vazios no lugar dos dados da loja.
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <CarregandoBloco altura="h-56" />
        <CarregandoFormulario campos={6} />
        <CarregandoFormulario campos={4} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Logo e Nome */}
      <div className="bg-card border rounded-xl p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-500" />
          Identidade da Empresa
        </h2>
        
        <div className="grid md:grid-cols-[200px_1fr] gap-6">
          {/* Upload de Logo */}
          <div>
            <label className="block text-sm font-medium mb-2">Logo (1080x1080)</label>
            <div className="relative">
              <div 
                className="w-full aspect-square rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center overflow-hidden cursor-pointer hover:border-purple-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.logo_url ? (
                  <img 
                    src={`${API_URL}${formData.logo_url}`}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center p-4">
                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Clique para fazer upload</p>
                  </div>
                )}
                
                {uploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>
              
              {formData.logo_url && (
                <button
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>

          {/* Nome e Slogan */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome do Sistema</label>
              <Input
                value={formData.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                placeholder="Ex: Núcleo"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Este nome aparecerá no canto superior esquerdo
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Slogan</label>
              <Input
                value={formData.slogan}
                onChange={(e) => handleChange("slogan", e.target.value)}
                placeholder="Ex: O Centro da sua Gestão"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dados da Empresa */}
      <div className="bg-card border rounded-xl p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Hash className="w-5 h-5 text-blue-500" />
          Dados Cadastrais
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome Fantasia</label>
            <Input
              value={formData.fantasy_name}
              onChange={(e) => handleChange("fantasy_name", e.target.value)}
              placeholder="Nome comercial"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Razão Social</label>
            <Input
              value={formData.legal_name}
              onChange={(e) => handleChange("legal_name", e.target.value)}
              placeholder="Razão social completa"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CNPJ</label>
            <Input
              value={formData.cnpj}
              onChange={(e) => handleChange("cnpj", e.target.value)}
              placeholder="00.000.000/0000-00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Data de Fundação</label>
            <Input
              type="date"
              value={formData.founding_date}
              onChange={(e) => handleChange("founding_date", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Inscrição Estadual</label>
            <Input
              value={formData.state_registration}
              onChange={(e) => handleChange("state_registration", e.target.value)}
              placeholder="Número da IE"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Inscrição Municipal</label>
            <Input
              value={formData.city_registration}
              onChange={(e) => handleChange("city_registration", e.target.value)}
              placeholder="Número da IM"
            />
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div className="bg-card border rounded-xl p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-500" />
          Endereço
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">CEP</label>
            <div className="relative">
              <Input
                value={formData.cep || ""}
                maxLength={9}
                onChange={(e) => {
                  const n = e.target.value.replace(/\D/g, "").slice(0, 8);
                  const fmt = n.length > 5 ? `${n.slice(0, 5)}-${n.slice(5)}` : n;
                  handleChange("cep", fmt);
                  if (n.length === 8) buscarCep(n);
                }}
                onBlur={(e) => buscarCep(e.target.value)}
                placeholder="00000-000"
              />
              {buscandoCep && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">preenche o resto sozinho</p>
          </div>
          <div className="col-span-2 md:col-span-3">
            <label className="block text-sm font-medium mb-1">Rua / Avenida</label>
            <Input
              value={formData.logradouro || ""}
              onChange={(e) => handleChange("logradouro", e.target.value)}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="block text-sm font-medium mb-1">Número</label>
            <Input
              value={formData.numero || ""}
              onChange={(e) => handleChange("numero", e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Bairro</label>
            <Input
              value={formData.bairro || ""}
              onChange={(e) => handleChange("bairro", e.target.value)}
            />
          </div>
          <div className="col-span-2 md:col-span-3">
            <label className="block text-sm font-medium mb-1">Cidade</label>
            <Input
              value={formData.cidade || ""}
              onChange={(e) => handleChange("cidade", e.target.value)}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="block text-sm font-medium mb-1">UF</label>
            <Input
              value={formData.uf || ""}
              maxLength={2}
              onChange={(e) => handleChange("uf", e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
            />
          </div>
        </div>
      </div>

      {/* Contato e Redes Sociais */}
      <div className="bg-card border rounded-xl p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-green-500" />
          Contato e Redes Sociais
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Telefone
            </label>
            <Input
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="(00) 00000-0000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Mail className="w-4 h-4" /> E-mail
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="contato@empresa.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Instagram className="w-4 h-4" /> Instagram
            </label>
            <Input
              value={formData.instagram}
              onChange={(e) => handleChange("instagram", e.target.value)}
              placeholder="@usuario"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Facebook className="w-4 h-4" /> Facebook
            </label>
            <Input
              value={formData.facebook}
              onChange={(e) => handleChange("facebook", e.target.value)}
              placeholder="facebook.com/pagina"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">TikTok</label>
            <Input
              value={formData.tiktok}
              onChange={(e) => handleChange("tiktok", e.target.value)}
              placeholder="@usuario"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Kwai</label>
            <Input
              value={formData.kwai}
              onChange={(e) => handleChange("kwai", e.target.value)}
              placeholder="@usuario"
            />
          </div>
        </div>
      </div>

      {/* Histórico importado de outro sistema */}
      <div className="bg-card rounded-xl border p-6 mb-6">
        <h3 className="font-semibold mb-1">Histórico importado</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Sistema de origem dos pedidos antigos importados. A logo escolhida aparece no card "Integração" da tela de Pedidos.
        </p>
        <div className="max-w-xs">
          <label className="block text-sm font-medium mb-1">Sistema de origem</label>
          <select
            value={formData.integration_source || ""}
            onChange={(e) => handleChange("integration_source", e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="">Nenhum</option>
            <option value="grandchef">Grandchef</option>
            <option value="anotaai">AnotaAi</option>
          </select>
        </div>
      </div>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Salvar Configurações
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ==================== ABA CONTROLE ====================

// ==================== ABA OPÇÕES ====================
